/**
 * Relay — App shell with Firebase Google OAuth gate.
 *
 * Flow:
 *   loading → SignIn → AllowlistCheck → TeamLog feed
 *
 * Per planner directive yNpJRJ7CseBeXhyN6PgA (EFISSUE-762, Apr 23). The
 * activity feed from commit 06ba2f2 is restored, wrapped in an auth check.
 * Allowlist is hardcoded for V1 (Travis is the only user); env-var
 * `VITE_RELAY_ALLOWED_EMAILS` (comma-separated) overrides if set.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import type { User } from "firebase/auth";
import { db } from "./lib/firebase";
import { postWithSequence } from "./lib/teamLog";
import { agentIdFromUser, getAuthHeader, getWorkspaceMembership, getAllWorkspaceMemberships, onUserChange, signOut, autoConfirmPendingMembership, InviteGoogleOnlyError, markFirstLogin } from "./lib/auth";
import { maybeWakeServices } from "./lib/wakeServices";
import { setSlugHash, slugFromHash } from "./lib/howRelayWorksHash";
import { LogView } from "./components/LogView";
import type { TeamLogStreamHandle } from "./components/TeamLogStream";
import { ERPinnedFilters } from "./components/ERPinnedFilters";
import { ERProjects } from "./components/ERProjects";
import { SidebarNav, SidebarNavRow, NavResetButton, useNavOrder, type NavRowDef } from "./components/SidebarNav";
import type { CCv2Tab } from "./components/ControlCenter";
// track:track-backlog--cc-v2-phase-2-sprint-board-tab--cc-v2-left-nav-item —
// CC v2 Sprint Board, wired to RelayDataProvider (Phase 1). Same
// lazy-load-on-first-open pattern v1 ControlCenter used before its
// track:dev-3-v1-decom-entry-point removal — v1's ControlCenter.tsx was
// deleted outright (track:cc-rebrand-pr1-cleanup) once confirmed dead.
const ControlCenter = lazy(() => import("./components/ControlCenter"));
// Tab-only content — none of these render until their centerView is selected
// (default view is "activity"), so code-splitting them keeps the initial
// bundle to just what first paint needs.
const ERDecisionsPanel = lazy(() => import("./components/ERDecisionsPanel").then(m => ({ default: m.ERDecisionsPanel })));
const HowRelayWorksPage = lazy(() => import("./components/HowRelayWorks/HowRelayWorksPage"));
const BacklogPage = lazy(() => import("./components/BacklogPage"));
const FeatureTestingPage = lazy(() => import("./components/FeatureTesting/FeatureTestingPage"));
const BackupMyGitPage = lazy(() => import("./components/BackupMyGit/BackupMyGitPage"));
import { ERSettingsModal } from "./components/ERSettingsModal";
// track-backlog--lazy-load-profile-and-project-background: rarely opened after
// initial setup, excluded from the initial bundle (same pattern as ControlCenter).
const ProfilePanel = lazy(() => import("./components/ProfilePanel").then(m => ({ default: m.ProfilePanel })));
const ProfilePage = lazy(() => import("./components/ProfilePage").then(m => ({ default: m.ProfilePage })));
const CharterEditor = lazy(() => import("./components/CharterEditor").then(m => ({ default: m.CharterEditor })));
const ProjectManagementSection = lazy(() => import("./components/ProjectManagementSection").then(m => ({ default: m.ProjectManagementSection })));
const WorkspaceGovernanceSection = lazy(() => import("./components/TeamArea").then(m => ({ default: m.WorkspaceGovernanceSection })));
import { TeamPage } from "./components/TeamPage";
import { RelayAdmin } from "./components/RelayAdmin";
import { CharterRoleCardsView } from "./components/CharterRoleCardsView";
import { fetchCharterRoleCards } from "./lib/charterRoleCards";
import { GetStartedPanel } from "./components/Onboarding/GetStartedPanel";
import { AuthScreen } from "./components/AuthScreen";
import { CreateWorkspaceGate } from "./components/CreateWorkspaceGate";
import { JoinWorkspace } from "./components/JoinWorkspace";
import { FloatingChat, type FloatingChatHandle } from "./components/FloatingChat";
import { FloatingNotesTasks } from "./components/FloatingNotesTasks";
import { BuildASpecModal } from "./components/ControlCenter/BuildASpecModal";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { DensityRail, MarkersRail, DecisionsMarkersRail, TeamPageMarkersRail, CharterMarkersRail, BacklogPageMarkersRail, WorkspaceGovernanceMarkersRail, ProjectManagementMarkersRail, AccountMarkersRail, TestAFeatureMarkersRail, ProjectBackgroundMarkersRail } from "./components/Rails";
import { parseViewMode, type ViewMode } from "./lib/chatFilter";
import { filterVisibleNames, filterVisibleTags, toggleProjectHidden } from "./lib/projectVisibility";
import { useTeamLogStream, oldestLoadedSequence } from "./hooks/useTeamLogStream";
import { useLogJump } from "./hooks/useLogJump";
import { useTagHistory } from "./hooks/useTagHistory";
import { fetchEarlierPage, serverTagForFilters } from "./lib/logJump";
import { LogJumpBanner, LogJumpNotice, logJumpMissingText } from "./components/LogJumpBanner";
import { useMentionables } from "./hooks/useMentionables";
import { useActionsBadgeCount } from "./hooks/useActionsBadgeCount";
import { getCachedMembership, setCachedMembership } from "./lib/appShellCache";
import { KNOWN_TAGS, tagPillStyle } from "./lib/tagColors";
import { computeMigration, slugify, type ProjectRecord, type ProjectBackground } from "./lib/projectsMigration";
import { fetchProjectBackground, saveProjectBackground } from "./lib/projectBackground";
const ProjectBackgroundModal = lazy(() => import("./components/ProjectBackgroundModal").then(m => ({ default: m.ProjectBackgroundModal })));
const ProjectBackgroundPage = lazy(() => import("./components/ProjectBackgroundPage").then(m => ({ default: m.ProjectBackgroundPage })));
import { mergeDecisionsWithHistory } from "./lib/mergeDecisions";
import {
  SDLC_FILTERS,
  SDLC_FILTERS_BY_KEY,
  type SDLCFilter,
} from "./lib/sdlcFilters";
import { subscribeWithReconnect } from "./lib/firestoreReconnect";
import { queueItemFromModalTrigger } from "./lib/RelayDataProvider";
import {
  DEFAULT_CHARTER_RULES,
  mergeCharterRules,
  needsSeed,
  type CharterRules,
} from "./lib/charterRules";
import type { TeamLogMessage, Decision } from "./types";
import { PageTabs } from "./components/PageTabs";

const FOCUS_COMPOSER_EVENT = "relay:focus-composer";
const FLASH_DURATION_MS = 1200;
const THEME_STORAGE_KEY = "relay_theme";
const DIRECT_CHIME_KEY = "relay_direct_chime_enabled";
const ACTION_CHIME_KEY = "relay_action_chime_enabled";
const SIDEBAR_COLLAPSED_KEY = "relay_sidebar_collapsed";
type SidebarCollapsed = { pinned?: boolean; always?: boolean };
type Theme = "light" | "dark";
function readInitialTheme(): Theme {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === "light" || raw === "dark") return raw;
  } catch {}
  return "dark";
}

function readInitialDirectChime(): boolean {
  try { return localStorage.getItem(DIRECT_CHIME_KEY) !== "0"; } catch { return true; }
}
function readInitialActionChime(): boolean {
  try { return localStorage.getItem(ACTION_CHIME_KEY) !== "0"; } catch { return true; }
}

// Chime now handled by ERModal component itself (removed playChime function)

const PAGE_SIZE = 200;
// Sprint 5 C2 (EFISSUE-811) — Decisions panel.
// Items live under `relay_decisions/{workspaceId}/items`.
const DECISIONS_SUBCOL = "items";
const DECISIONS_COL = "relay_decisions";

type CenterView = "activity" | "decisions" | "profile" | "charter" | "team" | "how-relay-works" | "backlog" | "governance" | "project-mgmt" | "relay-admin" | "feature-testing" | "backup-my-git" | "project-background";

// track:charter-owner-only (spec #66091) — whether the signed-in user is the
// platform owner comes from the server (GET /api/charter/role-cards), which
// compares against the PLATFORM_OWNER_UID setting. It only controls whether the
// nav item and view render; the server routes enforce it.


export default function App() {
  const [authLoading, setAuthLoading] = useState(true);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [membershipError, setMembershipError] = useState<string | null>(null);
  // Set when an invite was refused because the person did not sign in with
  // Google; shown on the sign-in screen after they are signed out.
  const [inviteNotice, setInviteNotice] = useState<string | null>(null);
  const [membership, setMembership] = useState<{ role: "lead" | "partner"; workspaceId: string } | null>(null);
  // track:multi-workspace-membership Phase 2 — every confirmed workspace
  // this signed-in uid belongs to, for the Account tab's switcher. Populated
  // non-blockingly, never gates first render or `membership` resolution
  // above — a plain list read alongside the existing flow, not a
  // replacement for it. workspaceName is enriched here (one extra doc read
  // per membership — bounded by how many workspaces a real account
  // realistically belongs to, not a scaling concern) rather than in
  // getAllWorkspaceMemberships() itself, which stays a pure identity/
  // membership mapping — display metadata is this component's concern.
  const [allMemberships, setAllMemberships] = useState<{ role: "lead" | "partner"; workspaceId: string; workspaceName: string }[]>([]);
  // track:multi-workspace-membership Phase 3 — was a plain `useState(false)`
  // with no code path anywhere that ever set it true, so JoinWorkspace
  // (including its own ?code= URL-param auto-join effect) never mounted —
  // a real invite link (https://easyrelay.app/?code=XXXX) silently fell
  // through to WorkspaceCreation instead, meaning a first-time invitee
  // would create a brand-new workspace rather than join the one they were
  // actually invited to. Mirrors JoinWorkspace.tsx's own existing
  // self-initializing pattern for reading this same URL param, so arriving
  // via a real invite link now correctly shows JoinWorkspace (which then
  // runs its already-working auto-join effect) instead of
  // WorkspaceCreation. Manual entry (no URL param) still starts on
  // WorkspaceCreation, with a link there to switch to JoinWorkspace.
  const [showJoin, setShowJoin] = useState(() => {
    if (typeof window !== "undefined") {
      return new URLSearchParams(window.location.search).has("code");
    }
    return false;
  });
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const memberRole = membership?.role ?? null;
  const activeWorkspaceId = membership?.workspaceId ?? "default";

  useEffect(() => {
    return onUserChange(async u => {
      setUser(u);
      setAuthLoading(false);
      if (u?.email) {
        maybeWakeServices();
        setMembershipError(null);

        // track:multi-workspace-membership Phase 2 — non-blocking, fired
        // and forgotten alongside (not awaited by) the membership
        // resolution below. A failure here only means the switcher list is
        // briefly empty/stale — it must never surface as membershipError or
        // block the primary sign-in flow, since it's purely additive UI
        // data, not a gate on using the app.
        getAllWorkspaceMemberships(u.uid)
          .then(async (memberships) => {
            const enriched = await Promise.all(
              memberships.map(async (m) => {
                let workspaceName = m.workspaceId;
                try {
                  const wsSnap = await getDoc(doc(db, "relay_workspaces", m.workspaceId));
                  const name = wsSnap.data()?.workspace_name;
                  if (typeof name === "string" && name.trim()) workspaceName = name;
                } catch {
                  // Keep the workspaceId fallback — a missing/unreadable
                  // name must never drop the workspace from the list.
                }
                return { ...m, workspaceName };
              })
            );
            setAllMemberships(enriched);
          })
          .catch((err) => console.error("[auth] getAllWorkspaceMemberships failed (non-fatal):", err));

        // Render from the last-confirmed membership instantly while the
        // real Firestore check (below) confirms/corrects it in the
        // background — breaks the Auth→membership→Team Log serial chain
        // that otherwise gates first render. See seq #26989.
        const email = u.email;
        const cached = await getCachedMembership(email);
        if (cached) {
          setMembership(cached);
          setMembershipLoading(false);
        } else {
          setMembershipLoading(true);
        }

        const applyMembership = async (result: { role: "lead" | "partner"; workspaceId: string } | null) => {
          setMembership(result);
          setMembershipLoading(false);
          if (result) {
            await setCachedMembership(email, result);
          }

          // Check for first login and post welcome message
          if (result && u.uid) {
            const firstLogin = await markFirstLogin(u.uid, result.workspaceId);
            setIsFirstLogin(firstLogin);
            // The welcome post is made by the server inside /api/auth/first-login.
          }
        };

        // Try direct membership lookup first — covers the common case
        // (already-confirmed member, the vast majority of loads after first
        // login) with a single Firestore read instead of two serial reads.
        // getWorkspaceMembership() short-circuits on a pending doc without
        // running its restricted fallback query (seq #22383), so it's safe
        // to call before checking for a pending invite.
        getWorkspaceMembership(u.email).then(async (result) => {
          if (result) {
            await applyMembership(result);
            return;
          }

          // Not yet confirmed — check for a pending invite. Its result
          // already carries {role, workspaceId} — use it directly instead of
          // re-querying Firestore via getWorkspaceMembership, whose fallback
          // `list` query is denied for any non-lead caller (seq #22383).
          const confirmResult = await autoConfirmPendingMembership(u);
          await applyMembership(confirmResult);
        }).catch((err) => {
          if (err instanceof InviteGoogleOnlyError) {
            setInviteNotice(err.message);
          } else {
            console.error("[auth] membership check failed:", err);
            setMembershipError("Could not verify workspace access. Please try again.");
          }
          setMembership(null);
          setMembershipLoading(false);
        });
      } else {
        setMembership(null);
        setAllMemberships([]);
        setMembershipError(null);
        setMembershipLoading(false);
      }
    });
  }, []);

  // cost-audit follow-up (seq #29900) — any click anywhere in the app (nav,
  // chat send, anything) is a signal the user is actively working, so it's
  // a reasonable moment to make sure the backend services are warm.
  // Capture phase + a plain document listener rather than per-element
  // handlers: covers every current and future clickable element with one
  // listener instead of having to remember to wire this into each one.
  // No-op while logged out (maybeWakeServices' own auth header would just
  // be empty and the server route would 401, so gating here avoids the
  // pointless request rather than for correctness).
  useEffect(() => {
    if (!user) return;
    const onAnyClick = () => maybeWakeServices();
    document.addEventListener("click", onAnyClick, true);
    return () => document.removeEventListener("click", onAnyClick, true);
  }, [user]);

  const retryMembershipCheck = useCallback(() => {
    if (!user?.email) return;
    setMembershipError(null);
    setMembershipLoading(true);
    getWorkspaceMembership(user.email).then(result => {
      setMembership(result);
      setMembershipLoading(false);
    }).catch((err) => {
      console.error("[auth] membership check failed:", err);
      setMembershipError("Could not verify workspace access. Please try again.");
      setMembership(null);
      setMembershipLoading(false);
    });
  }, [user]);

  if (authLoading || membershipLoading) {
    return <ConnectingSplash />;
  }

  if (!user) {
    return <AuthScreen inviteNotice={inviteNotice} />;
  }

  if (memberRole === null) {
    if (membershipError) {
      return (
        <CenteredCard>
          <div style={{ fontSize: 13, color: "var(--tag-blocker)", marginBottom: 12, textAlign: "center" }}>
            {membershipError}
          </div>
          <button
            onClick={retryMembershipCheck}
            style={{ background: "var(--accent)", color: "#fff", border: 0, borderRadius: 6, padding: "8px 18px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
          >
            Retry
          </button>
          <button
            onClick={() => signOut()}
            style={{ marginTop: 8, background: "none", border: 0, color: "var(--text-muted)", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}
          >
            Sign out
          </button>
        </CenteredCard>
      );
    }
    if (showJoin) {
      return (
        <JoinWorkspace
          email={user.email ?? ""}
          onJoined={(wsId, role) => setMembership({ role, workspaceId: wsId })}
          onSignOut={() => signOut()}
          onShowCreate={() => setShowJoin(false)}
        />
      );
    }
    return (
      <CreateWorkspaceGate
        user={user}
        onCreated={(wsId, role) => setMembership({ role: role ?? "lead", workspaceId: wsId })}
        onSignOut={() => signOut()}
        onShowJoin={() => setShowJoin(true)}
      />
    );
  }

  return (
    <TeamLogShell
      user={user}
      memberRole={memberRole}
      workspaceId={activeWorkspaceId}
      isFirstLogin={isFirstLogin}
      allMemberships={allMemberships}
      onSwitchWorkspace={(wsId, role) => setMembership({ role, workspaceId: wsId })}
      onWorkspaceCreated={(entry) =>
        setAllMemberships((prev) =>
          prev.some((m) => m.workspaceId === entry.workspaceId) ? prev : [...prev, entry]
        )
      }
    />
  );
}

function ConnectingSplash() {
  return (
    <div style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#0f1117" }}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="350 325 315 315" width="80" height="80">
        <style>{`
        .rb{animation:rchase 1.6s linear infinite;fill:#D8D5C9}
        .rb-teal{--rc:#1A6B6B}
        .rb-orange{--rc:#EB964B}
        .rb-mint{--rc:#7BCBBA}
        .rb-yellow{--rc:#EED249}
        @keyframes rchase{0%,100%{fill:#D8D5C9}5%,11%{fill:var(--rc)}}
        .rb[data-p="0"]{animation-delay:0s}
        .rb[data-p="1"]{animation-delay:-0.229s}
        .rb[data-p="2"]{animation-delay:-0.457s}
        .rb[data-p="3"]{animation-delay:-0.686s}
        .rb[data-p="4"]{animation-delay:-0.914s}
        .rb[data-p="5"]{animation-delay:-1.143s}
        .rb[data-p="6"]{animation-delay:-1.371s}
        `}</style>
        <path fill="#1A6B6B" d="M506.954 387.143C505.194 386.585 503.478 385.898 501.82 385.087C495.998 382.287 491.076 377.09 489.084 370.887C487.096 364.776 487.723 358.116 490.815 352.483C493.912 346.834 499.198 342.708 505.43 341.074C534.305 333.489 548.849 379.645 514.048 386.774C514.136 400.458 514.468 417.714 513.908 431.234C514.551 432.918 517.883 432.563 520.076 432.964C530.926 434.947 538.411 439.411 546.457 446.598C554.188 439.4 564.638 427.798 572.388 419.855C573.898 421.526 575.639 422.973 577.557 424.152C570.54 431.392 556.714 444.755 550.677 451.983C557.09 461.586 559.882 467.274 560.828 478.954L604.609 478.951C604.531 480.932 604.394 483.122 604.444 485.088L576.945 484.857C573.557 484.834 563.761 484.666 560.88 485.247C559.095 498.454 556.547 503.768 548.621 514.346C558.182 524.614 568.225 534.377 577.757 544.671C575.945 545.795 574.294 547.16 572.848 548.727C566.788 542.628 560.794 536.463 554.867 530.234C551.778 526.999 547.176 521.883 543.909 519.071C534.055 525.986 526.747 529.947 514.486 530.655L514.544 582.862C512.09 582.689 510.741 583.057 507.894 582.653C507.698 565.317 507.693 547.979 507.879 530.642C494.78 529.472 488.119 525.595 477.888 517.952C468.293 528.053 458.6 538.059 448.81 547.97C447.207 546.361 445.38 544.993 443.385 543.906C453.226 533.72 463.17 523.633 473.215 513.649C466.053 503.818 463.508 497.65 462.228 485.489C446.467 485 428.949 485.358 413.122 485.384C413.428 483.283 413.441 481.149 413.163 479.044L462.285 478.946C462.999 468.535 466.153 460.966 471.847 452.457C465.345 444.605 452.879 432.743 445.396 424.612C447.206 423.43 448.847 422.006 450.272 420.38C458.334 429.193 467.779 439.085 476.5 447.291C485.065 438.859 494.821 433.084 507.1 432.379C506.706 427.748 506.903 420.659 506.908 415.821L506.954 387.143Z" />
        <path className="rb rb-teal" data-p="0" d="M445.396 424.612C434.937 431.443 420.975 429.007 413.447 419.037C405.92 409.067 407.4 394.971 416.834 386.783C426.268 378.594 440.432 379.111 449.244 387.965C458.056 396.819 458.505 410.986 450.272 420.38C448.847 422.006 447.206 423.43 445.396 424.612Z" />
        <path className="rb rb-orange" data-p="1" d="M572.388 419.855C564.049 410.629 564.258 396.527 572.868 387.553C581.477 378.579 595.559 377.785 605.122 385.735C614.686 393.685 616.478 407.675 609.228 417.779C601.978 427.883 588.151 430.666 577.557 424.152C575.639 422.973 573.898 421.526 572.388 419.855Z" />
        <path className="rb rb-mint" data-p="2" d="M577.757 544.671C588.319 538.118 602.152 540.868 609.403 550.963C616.655 561.057 614.845 575.045 605.263 582.961C595.681 590.878 581.604 590.017 573.058 580.992C564.512 571.966 564.42 557.863 572.848 548.727C574.294 547.16 575.945 545.795 577.757 544.671Z" />
        <path className="rb rb-orange" data-p="3" d="M448.81 547.97C457.544 556.732 458.027 570.754 449.916 580.096C441.805 589.438 427.855 590.929 417.953 583.512C408.05 576.094 405.559 562.288 412.244 551.878C418.929 541.467 432.521 537.988 443.385 543.906C445.38 544.993 447.207 546.361 448.81 547.97Z" />
        <path className="rb rb-teal" data-p="4" d="M604.609 478.951C607.966 465.252 618.827 455.911 633.393 459.493C639.637 461.023 644.996 465.015 648.25 470.56C651.389 475.989 652.122 482.482 650.271 488.475C646.324 501.522 633.229 508.427 620.391 504.017C614.334 501.913 609.406 497.418 606.754 491.58C605.769 489.434 605.327 487.771 604.777 485.499L575.219 485.409C571.907 485.398 563.872 485.578 560.88 485.247C563.761 484.666 573.557 484.834 576.945 484.857L604.444 485.088C604.394 483.122 604.531 480.932 604.609 478.951Z" />
        <path className="rb rb-yellow" data-p="5" d="M507.894 582.653C510.741 583.057 512.09 582.689 514.544 582.862C515.368 582.988 516.189 583.137 517.005 583.31C522.819 584.585 528.198 588.695 531.288 593.749C534.648 599.257 535.653 605.884 534.08 612.141C532.513 618.375 528.422 623.674 522.787 626.767C511.56 632.975 497.435 628.545 491.315 617.12C485.242 605.78 489.393 591.908 500.66 585.655C502.517 584.624 505.165 583.976 507.199 582.993L507.894 582.653Z" />
        <path className="rb rb-yellow" data-p="6" d="M413.122 485.384C411.379 497.378 400.65 505.976 388.565 505.064C376.479 504.151 367.162 494.04 367.24 481.92C367.317 469.801 376.763 459.809 388.859 459.052C400.955 458.294 411.573 467.028 413.163 479.044C413.441 481.149 413.428 483.283 413.122 485.384Z" />
      </svg>
      <div style={{ marginTop: 20, fontFamily: "system-ui,-apple-system,sans-serif", fontSize: 11, color: "#4a5568", letterSpacing: "0.15em", textTransform: "uppercase" }}>
        connecting
      </div>
    </div>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="h-screen w-screen flex items-center justify-center"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <div
        className="flex flex-col items-center gap-4 px-6 py-10"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          maxWidth: 420,
        }}
      >
        <span
          className="font-mono text-lg lowercase tracking-tight"
          style={{ color: "var(--text)" }}
        >
          relay
        </span>
        <div className="text-sm text-center" style={{ color: "var(--text2)" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// The prior `SignInScreen` (bare "Sign in with Google" card) was replaced
// by `<ERLanding onGetStarted={…}>` as the pre-auth entry point per
// directive `HPKeAJpJA8K04a2NABGM`. Sign-in errors now surface as a
// fixed-position banner over the landing rather than inside the card.


// Derives a stable relay_modal_queue item ID from a relay_modal/default
// trigger doc's own content, rather than a random auto-ID. Firestore rules
// for relay_modal/{userId} are server-write-only (`allow write: if false`),
// so the client's own deleteDoc on that trigger always fails
// permission-denied and the doc is never actually removed — every
// onSnapshot reconnect redelivers the same still-existing doc. Keying the
// queue item off the trigger's own content means a redelivered trigger
// converts to the same item every time (a no-op once it exists) instead of
// a fresh duplicate — incident: a single show_modal() call produced 500+
// duplicate "pending" items in under 90 minutes without this (2026-08-19,
// seq #31346/#31351).
//
// 2026-08-27 follow-up — timestamp alone only dedupes *redeliveries of one
// write*; it does nothing for genuinely separate show_modal() calls about
// the same real-world blocker (e.g. adk-relay's daemon crash-looping and
// re-firing the same blocker on every restart — backlog #37689 and
// follow-ups). Each such call gets a fresh timestamp, so it always produced
// a brand-new queue item — confirmed live: 361 stale relay_modals/blockers
// docs and dozens of near-identical "X is blocked on Y" cards for the same
// track, some going back to 2026-06-16. adk-relay's fire_blocker_modal()
// tags its "Resolve Blocker" action with `seq:{N}` (the blocker's own Team
// Log sequence number) specifically so a stable identity is available here
// — prefer that when present, since it correctly collapses both
// redeliveries AND genuine re-fires of the same blocker; fall back to the
// timestamp (unchanged prior behavior) for modal types that don't carry it.
export function deriveModalTriggerKey(data: { timestamp?: unknown; actions?: unknown }): string {
  if (Array.isArray(data.actions)) {
    for (const action of data.actions) {
      const tags = (action as { tags?: unknown })?.tags;
      if (!Array.isArray(tags)) continue;
      const seqTag = tags.find((t): t is string => typeof t === "string" && /^seq:\d+$/.test(t));
      if (seqTag) return seqTag;
    }
  }
  return typeof data.timestamp === "string" && data.timestamp
    ? data.timestamp
    : String(Date.now());
}

// Helper function to get author color for timeline dots (Track 4)
function TeamLogShell({ user, memberRole, workspaceId, isFirstLogin, allMemberships, onSwitchWorkspace, onWorkspaceCreated }: { user: User; memberRole: "lead" | "partner"; workspaceId: string; isFirstLogin?: boolean; allMemberships: { role: "lead" | "partner"; workspaceId: string; workspaceName: string }[]; onSwitchWorkspace: (workspaceId: string, role: "lead" | "partner") => void; onWorkspaceCreated: (entry: { role: "lead"; workspaceId: string; workspaceName: string }) => void }) {
  // S2-4 PR-3 — single source for the live team_log stream with
  // optimistic-insert plumbing exposed for the composer.
  const {
    messages,
    loaded,
    loadedFromCache,
    error,
    optimisticInsert,
    optimisticRollback,
  } = useTeamLogStream(workspaceId);

  // Sprint 5 C2 (EFISSUE-811) — center-column router. Activity Log and
  // Decisions are sibling views; sidebar nav swaps between them. Default to
  // activity so existing-user behavior is unchanged on first load. No
  // localStorage persistence in V1 — refresh resets to activity.
  //
  // Sprint 6 C3 (EFISSUE-820) — `decisionsProjectFilter` so a click on a
  // project's Decisions sub-row navigates to Decisions and pre-applies
  // that project's filter. (The sibling "artifacts" placeholder route +
  // `artifactsProjectFilter` were removed — ui/left-nav-filter-cleanup —
  // dead since Sprint 48 dropped the Artifacts SDLC row, see sdlcFilters.ts.
  // "release-log" + `releaseLogEntries` were removed the same way —
  // track:remove-release-log-nav — redundant with the Relay Log's own
  // filter, per Travis.)
  const [centerView, setCenterView] = useState<CenterView>("activity");
  const [decisionsProjectFilter, setDecisionsProjectFilter] = useState<string | null>(null);
  const [backlogProjectFilter, setBacklogProjectFilter] = useState<string | null>(null);
  const [backlogFilteredItems, setBacklogFilteredItems] = useState<import('./components/BacklogPage').BacklogItem[]>([]);
  const mainScrollRef = useRef<HTMLElement>(null);

  const [charterScrollEl, setCharterScrollEl] = useState<HTMLDivElement | null>(null);
  const [governanceScrollEl, setGovernanceScrollEl] = useState<HTMLDivElement | null>(null);
  const [projectMgmtScrollEl, setProjectMgmtScrollEl] = useState<HTMLDivElement | null>(null);
  const [teamScrollEl, setTeamScrollEl] = useState<HTMLDivElement | null>(null);
  const [profileScrollEl, setProfileScrollEl] = useState<HTMLDivElement | null>(null);
  // track:test-a-feature-all-projects-nav — same onScrollReady/rail pattern
  // as the other pages above; featureTestingSections is reported up by the
  // page itself (empty outside All Projects mode), same mechanism as
  // ReleaseLogPage's onEntriesLoaded/BacklogPage's onItemsFiltered.
  const [featureTestingScrollEl, setFeatureTestingScrollEl] = useState<HTMLDivElement | null>(null);
  const [featureTestingSections, setFeatureTestingSections] = useState<{ id: string; label: string; desc: string }[]>([]);
  // track:project-background-page-rail — same onScrollReady/rail pattern as
  // featureTesting above; projectBackgroundSections is reported up by the
  // page itself (empty outside "All Projects" mode).
  const [projectBackgroundScrollEl, setProjectBackgroundScrollEl] = useState<HTMLDivElement | null>(null);
  const [projectBackgroundSections, setProjectBackgroundSections] = useState<{ id: string; label: string; desc: string }[]>([]);
  const charterScrollRef = useMemo(() => ({ current: charterScrollEl as Element | null }), [charterScrollEl]);
  const governanceScrollRef = useMemo(() => ({ current: governanceScrollEl as Element | null }), [governanceScrollEl]);
  const projectMgmtScrollRef = useMemo(() => ({ current: projectMgmtScrollEl as Element | null }), [projectMgmtScrollEl]);
  const teamScrollRef = useMemo(() => ({ current: teamScrollEl as Element | null }), [teamScrollEl]);
  const profileScrollRef = useMemo(() => ({ current: profileScrollEl as Element | null }), [profileScrollEl]);
  const featureTestingScrollRef = useMemo(() => ({ current: featureTestingScrollEl as Element | null }), [featureTestingScrollEl]);
  const projectBackgroundScrollRef = useMemo(() => ({ current: projectBackgroundScrollEl as Element | null }), [projectBackgroundScrollEl]);
  // S2-4 PR-3a (REQ-2) — viewMode is a pure derivation of the URL
  // pathname. Single source of truth. `/log` → log view; everything
  // else (default `/`) → chat view. Legacy `?view=chat|log` URLs are
  // rewritten to the canonical pathname synchronously during state
  // init so initial render is never inconsistent with the URL.
  const [, setPathname] = useState<string>(() => {
    if (typeof window === "undefined") return "/";
    const search = window.location.search;
    const explicitView =
      search.includes("view=chat") || search.includes("view=log");
    if (explicitView) {
      const parsed = parseViewMode(search);
      const target = parsed === "chat" ? "/" : "/log";
      const url = new URL(window.location.href);
      url.searchParams.delete("view");
      window.history.replaceState(
        null,
        "",
        target + (url.search || "") + url.hash,
      );
      return target;
    }
    return window.location.pathname;
  });
  // Activity Log is always unfiltered log mode. ChatWindow filtering moved to FloatingChat only.
  const viewMode: ViewMode = "log";
  // Browser back/forward fires `popstate`; programmatic navigation via
  // Sync pathname state from URL (for popstate events)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sync = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", sync);
    window.addEventListener("relay:route-change", sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener("relay:route-change", sync);
    };
  }, []);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [decisionsLoaded, setDecisionsLoaded] = useState(false);
  const [decisionsError, setDecisionsError] = useState<string | null>(null);
  const [bqDecisionRows, setBqDecisionRows] = useState<Array<{ sequence: number; author: string; content: string; timestamp: string; tags: string[] }>>([]);
  const [bqDecisionsLoaded, setBqDecisionsLoaded] = useState(false);
  const [bqDecisionsLoading, setBqDecisionsLoading] = useState(false);
  const [bqDecisionsError, setBqDecisionsError] = useState<string | null>(null);
  // track-backlog--perf-defer-useattentioncount-decisions-listener-rate-limit-listener-from-page-load-mount:
  // gated on centerView==='decisions' — same defer pattern already used below
  // for the BQ decisions history fetch — so this live listener no longer
  // fires unconditionally on every page load.
  useEffect(() => {
    if (centerView !== "decisions") return;
    const itemsCol = collection(db, DECISIONS_COL, workspaceId, DECISIONS_SUBCOL);
    const q = query(itemsCol, orderBy("createdAt", "desc"), limit(PAGE_SIZE));
    return subscribeWithReconnect(({ retry, markConnected }) =>
      onSnapshot(
        q,
        (snap) => {
          markConnected();
          const next: Decision[] = snap.docs.map((d) => {
            const data = d.data() as Omit<Decision, "id">;
            return { id: d.id, ...data };
          });
          setDecisions(next);
          setDecisionsLoaded(true);
        },
        (err) => {
          // Surface the error in the panel instead of taking the whole shell
          // down — users can still use Activity Log if Decisions reads fail.
          console.error("[Relay] decisions subscription failed:", err);
          setDecisionsError(err.message || "Failed to load decisions");
          setDecisionsLoaded(true);
          retry();
        },
      ),
    );
  }, [centerView]);

  // Lazy-load full decisions history from BigQuery when Decisions tab opens.
  // Fires once per session; BQ query returns all ~20k decision-tagged messages.
  useEffect(() => {
    if (centerView !== "decisions" || bqDecisionsLoaded || bqDecisionsLoading) return;
    setBqDecisionsLoading(true);
    setBqDecisionsError(null);
    getAuthHeader()
      .then((headers) => fetch("/api/decisions/history", { headers }))
      .then((r) => r.json())
      .then((data: { decisions?: any[]; error?: string }) => {
        if (data.error) throw new Error(data.error);
        setBqDecisionRows(data.decisions ?? []);
        setBqDecisionsLoaded(true);
      })
      .catch((err: Error) => {
        console.error("[Relay] BQ decisions history failed:", err.message);
        setBqDecisionsError(err.message || "Failed to load full history");
        setBqDecisionsLoaded(true);
      })
      .finally(() => setBqDecisionsLoading(false));
  }, [centerView, bqDecisionsLoaded, bqDecisionsLoading]);

  // Theme toggle per planner directive `PC6PazogzxBcZNqVFgEL`. Writes
  // `<html data-theme="light|dark">` so the CSS variable blocks in
  // index.css flip on without a class swap. localStorage persists
  // across sessions. Default is dark for existing users.
  const [theme, setTheme] = useState<Theme>(readInitialTheme);
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try { localStorage.setItem(THEME_STORAGE_KEY, theme); } catch {}
  }, [theme]);
  const toggleTheme = () => setTheme(t => (t === "dark" ? "light" : "dark"));

  const [directChimeEnabled, setDirectChimeEnabled] = useState<boolean>(readInitialDirectChime);
  const directChimeEnabledRef = useRef(directChimeEnabled);
  useEffect(() => { directChimeEnabledRef.current = directChimeEnabled; }, [directChimeEnabled]);
  const toggleDirectChime = () => setDirectChimeEnabled(v => {
    const next = !v;
    try { localStorage.setItem(DIRECT_CHIME_KEY, next ? "1" : "0"); } catch {}
    return next;
  });

  const [actionChimeEnabled, setActionChimeEnabled] = useState<boolean>(readInitialActionChime);
  const actionChimeEnabledRef = useRef(actionChimeEnabled);
  useEffect(() => { actionChimeEnabledRef.current = actionChimeEnabled; }, [actionChimeEnabled]);
  const toggleActionChime = () => setActionChimeEnabled(v => {
    const next = !v;
    try { localStorage.setItem(ACTION_CHIME_KEY, next ? "1" : "0"); } catch {}
    return next;
  });

  // Gate Web Audio on first user interaction (browser autoplay policy)
  const hasInteractedRef = useRef(false);
  useEffect(() => {
    const mark = () => { hasInteractedRef.current = true; };
    window.addEventListener("click", mark, { once: true });
    window.addEventListener("keydown", mark, { once: true });
    return () => {
      window.removeEventListener("click", mark);
      window.removeEventListener("keydown", mark);
    };
  }, []);

  // Tone A — direct mention chime (880 Hz sine, 350ms)
  const playDirectChime = useCallback(() => {
    if (!hasInteractedRef.current) return;
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.35);
      osc.onended = () => ctx.close();
    } catch {}
  }, []);

  // Tone B — action alert chime (660→880 Hz triangle, 450ms total, distinct from Tone A)
  const playActionChime = useCallback(() => {
    if (!hasInteractedRef.current) return;
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "triangle";
      osc.frequency.setValueAtTime(660, ctx.currentTime);
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.18);
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.45);
      osc.onended = () => ctx.close();
    } catch {}
  }, []);

  // Onboarding progress flags — set when the user takes the relevant action
  useEffect(() => {
    if (centerView === "charter") {
      try { localStorage.setItem("relay_onboarding_charter", "1"); } catch {}
    }
  }, [centerView]);

  // EFISSUE-793: scroll-to-bottom button visibility tracks the same scroll
  // event the auto-scroll-on-new-message logic uses, but with a larger
  // threshold (>200px from bottom) so it doesn't flicker on small scrolls.
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  // Sprint `IfXyyJg01e1ox2VAIZIU`, Commit A — active tag filters. State
  // lives at the shell level so both the stream's inline pill clicks and
  // the Action Required panel's tag search (Commit B) share the same
  // set. Insertion order preserved for stable rendering in the filter
  // bar. Resets on refresh per spec; no persistence in V1.
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [tagQuery, setTagQuery] = useState("");

  // track:log-jump — a #seq search opens a separate jump view (post + its
  // neighbours from GET /api/messages/window) instead of merging one old post
  // into the live list. The live listener and `earlierMessages` stay untouched.
  const logJump = useLogJump();
  // ui/left-nav-project-tag-fix — the BQ log-search effect that used to
  // live here was moved below `projectNames`'s declaration (it needs that
  // value; `projectNames` in turn needs `projects`, declared later in this
  // component) — see the effect just after `projectNames` below.

  // EFISSUE-838 — single compound filter (project tag AND sub-filter tag).
  // Lives parallel to activeFilters. When set, it shadows activeFilters in
  // the message panel — clicking a sub-filter is meant to "narrow to this
  // exact intersection," not pile onto whatever was already pinned.
  // Setting either filter mode clears the other so the active state stays
  // unambiguous.
  const [compoundFilter, setCompoundFilter] = useState<{ project: string; subFilter: string } | null>(null);
  // EFISSUE-839 — fixed nav filter mode for the Help view. Mutually
  // exclusive with activeFilters and compoundFilter — at most one is set
  // at a time. (`notes`/`reminders` modes removed along with the My
  // Notes/My Reminders nav rows; `action-required`/`assigned-to-me` modes
  // from relay-42 Phase 2a were removed earlier — ui/left-nav-filter-
  // cleanup — never had a nav row wired up; ControlCenter's Active
  // Blockers / Findings Requiring Response sections are the real, shipped
  // equivalent.)
  type NavFilterMode = "help";
  const [navFilter, setNavFilter] = useState<NavFilterMode | null>(null);
  // relay-S3-sdlcfilters — per-project fixed SDLC sub-row state. Mutually
  // exclusive with `compoundFilter` and `navFilter`. Holds {project,key} so
  // we can highlight the active row in ERProjects and derive the OR-mode
  // tag set passed to TeamLogStream. Activity is the "key === 'activity'"
  // case — we still set this state so the row highlights correctly even
  // though its tag set is empty.
  const [sdlcFilter, setSdlcFilter] = useState<{ project: string; key: string } | null>(null);
  // Each of these handlers operates on the activity-stream filter
  // surface — they intentionally drop the user back into the Activity
  // view (centerView="activity") even if they're currently looking at
  // Decisions. Without this, clicking e.g. "Relay > Activity"
  // after "Relay > Decisions" left the Decisions panel up while the
  // filter state silently changed underneath it (#1358 / #1359 repro).
  // The dedicated openProjectDecisions handler is the path that
  // switches view; filter handlers do not.
  // relay-S3-uxfix Fix 1 — mutually exclusive project filters. When the
  // incoming tag matches a current project name (e.g. clicking Juju/Activity
  // after Relay/Activity), drop other project-name tags from activeFilters
  // before appending the new one so the AND-mode stream isn't gated on two
  // mutually exclusive project tags. Non-project tags keep additive behavior
  // (`pre-flight`, `blocker`, etc. continue to stack as before). Toggling
  // the same project name off keeps the existing remove behavior.
  const toggleFilter = (tag: string) => {
    setCompoundFilter(null);
    setNavFilter(null);
    setSdlcFilter(null);
    setActiveFilters(prev => {
      if (prev.includes(tag)) return prev.filter(t => t !== tag);
      const projectNames = new Set(projects.map(p => p.name));
      const isProjectTag = projectNames.has(tag);
      const base = isProjectTag ? prev.filter(t => !projectNames.has(t)) : prev;
      return [...base, tag];
    });
    setCenterView("activity");
  };
  const clearFilters = () => {
    setActiveFilters([]);
    setCompoundFilter(null);
    setNavFilter(null);
    setSdlcFilter(null);
    setCenterView("activity");
  };
  const toggleSubFilter = useCallback((projectName: string, subFilter: string) => {
    setCompoundFilter(prev => {
      if (prev?.project === projectName && prev.subFilter === subFilter) return null;
      return { project: projectName, subFilter };
    });
    // Pinned/active filter set is mutually exclusive with compound mode.
    setActiveFilters([]);
    setNavFilter(null);
    setSdlcFilter(null);
    setCenterView("activity");
  }, []);
  // EFISSUE-839 — toggle handler for Help. Clicking the same mode again
  // clears it (back to default activity stream).
  const toggleNavFilter = useCallback((mode: NavFilterMode) => {
    setNavFilter(prev => (prev === mode ? null : mode));
    setActiveFilters([]);
    setCompoundFilter(null);
    setSdlcFilter(null);
    setCenterView("activity");
  }, []);
  // Effective filter list passed to TeamLogStream. Mode precedence: nav
  // filter > compound filter > pinned/active filters.
  const effectiveFilters = useMemo(
    () => {
      if (navFilter === "help") return ["relay-help"];
      if (compoundFilter) return [compoundFilter.project, compoundFilter.subFilter];
      // relay-S3-sdlcfilters / UI-D fix (superseded — ui/left-nav-project-tag-fix):
      // this used to return [] because requiring the project name as an AND
      // tag emptied every filter — the real cause (bare-name-only matching
      // against tags that are now `project:`-prefixed per Charter's Sprint 80
      // convention) is fixed in TeamLogStream's `projectNames`-aware OR match,
      // so the project scope is restored here instead of dropped. Without
      // this, Requirements/Backlog rows matched every project's items
      // workspace-wide instead of just the clicked project's.
      if (sdlcFilter) return [sdlcFilter.project];
      return activeFilters;
    },
    [navFilter, compoundFilter, sdlcFilter, activeFilters],
  );
  // track:track-backlog--p0-white-page-when-clicking-project-in-left-nav--requires-browser-refresh
  // — remount key for the center-view ErrorBoundary below. Changes whenever
  // the user switches project/view context so a caught render error clears
  // itself the moment they navigate elsewhere, instead of staying blank
  // until a manual browser refresh.
  const centerViewResetKey = useMemo(
    () =>
      [centerView, activeFilters.join(","), compoundFilter?.project ?? "", compoundFilter?.subFilter ?? "",
        sdlcFilter?.project ?? "", sdlcFilter?.key ?? "", navFilter ?? "", decisionsProjectFilter ?? ""].join("|"),
    [centerView, activeFilters, compoundFilter, sdlcFilter, navFilter, decisionsProjectFilter],
  );
  // relay-S3-sdlcfilters — OR-mode tag set derived from the active
  // SDLC sub-row. Activity (`tags === []`) and a null sdlcFilter both
  // resolve to null, which the stream treats as a no-op.
  const sdlcTagSet = useMemo<string[] | null>(() => {
    if (!sdlcFilter) return null;
    const cfg = SDLC_FILTERS_BY_KEY.get(sdlcFilter.key);
    if (!cfg || cfg.tags.length === 0) return null;
    return cfg.tags;
  }, [sdlcFilter]);
  // Current user's author string. Email local-part lowercase, matches
  // ERComposerV1's deriveAuthor. Passed to TeamLogStream as selfAuthor.
  const myAuthor = useMemo(() => {
    const e = user.email ?? "";
    const at = e.indexOf("@");
    return (at > 0 ? e.slice(0, at) : e || "unknown").toLowerCase();
  }, [user.email]);

  // Sprint 2 Commit 2 (`FGrrZUydSmB9HyRJ0KkA`) — acknowledged state now
  // lives on each Firestore document (`acknowledged_by: string[]` +
  // `acknowledged_at: number`). No more localStorage, no more cross-tab
  // storage listener; the onSnapshot subscription above is already the
  // source of truth for everything the panel needs, so the UI converges
  // across devices automatically.
  const myUid = user.uid;

  // Sprint 2 Commit 3 (`FGrrZUydSmB9HyRJ0KkA`) — load-earlier pagination.
  // The onSnapshot listener is capped at PAGE_SIZE=200 so users can only
  // see the latest window by default. Clicking "Load earlier messages"
  // runs a one-shot getDocs for the next 200 before the current oldest
  // visible message and prepends them into `earlierMessages`. Real-time
  // listener stays untouched — new messages continue to append to the
  // `messages` half of the render. Declared here (before acknowledgedIds)
  // so that the ack compute can see the paginated history too.
  const [earlierMessages, setEarlierMessages] = useState<TeamLogMessage[]>([]);
  const [hasMoreEarlier, setHasMoreEarlier] = useState(true);
  const [loadingEarlier, setLoadingEarlier] = useState(false);

  // Track 16 — Tier limit awareness: rate limit data and warning state
  const [rateLimitData, setRateLimitData] = useState<{
    count: number;
    limit: number;
    tier: string;
  } | null>(null);
  const [usageWarningShown, setUsageWarningShown] = useState<boolean>(false);

  const allMessages = useMemo(() => {
    return [...earlierMessages, ...messages];
  }, [earlierMessages, messages]);
  // relay-S3-uxfix Fix 4 — hybrid Decisions panel. BigQuery history rows
  // (latest 500) are unioned with the live Firestore window and the structured
  // decisions, de-duplicated by sequence, so decisions posted while the view
  // is open still appear. `decisions` state is not modified so open decision
  // counts elsewhere remain accurate.
  const mergedDecisions = useMemo(
    () => mergeDecisionsWithHistory(decisions, allMessages, bqDecisionRows),
    [decisions, allMessages, bqDecisionRows],
  );

  // Onboarding: mark first-message milestone once the log has any content
  useEffect(() => {
    if (allMessages.length > 0) {
      try { localStorage.setItem("relay_onboarding_message", "1"); } catch {}
    }
  }, [allMessages.length]);

  // Sprint 4 Commit 3 (`gvVCRKtvjiuK7VqlXXql`) — pinned tag filters.
  // Workspace state lives at `relay_workspaces/default.pinned_filters`.
  // Subscribed here so pins added on one device show up on another
  // without a refresh. Auto-upsert on first touch means planner's
  // "no explicit init step" requirement holds.
  //
  // Sprint 6 C3 (EFISSUE-820) — extended the same listener to read
  // `projects` from the workspace doc. One-time migration moves
  // `relay` and `juju` from `pinned_filters` to `projects` on first
  // load; `migrationDone` is a per-tab guard so the snapshot's own
  // re-fire doesn't trigger a second write.
  const [pinnedFilters, setPinnedFilters] = useState<string[]>([]);
  const [projects, setProjects] = useState<ProjectRecord[]>([]);
  // track:track-backlog--cc-composer-project-tag-validator — mirrors the
  // same `active_project_slug` field ControlCenter's project switcher
  // writes, so the composer can suggest that project without a second
  // Firestore listener.
  const [activeProjectSlug, setActiveProjectSlug] = useState<string | null>(null);
  // Sprint 51 EFISSUE bridge — derives the active project for the header
  // label from whatever plain single-project tag filter is currently set,
  // regardless of whether it was set via the sidebar or the Control Center
  // dropdown. Compound/nav/sdlc modes and multi-tag filters aren't a single
  // project context, so they resolve to null (header shows nothing).
  const activeProjectName = useMemo(() => {
    if (navFilter || compoundFilter || sdlcFilter) return null;
    if (activeFilters.length === 1 && projects.some(p => p.name === activeFilters[0])) {
      return activeFilters[0];
    }
    return null;
  }, [activeFilters, navFilter, compoundFilter, sdlcFilter, projects]);
  // relay-42 Phase 2b (DEC-26) — Settings → Projects toggles which
  // projects appear in the sidebar Pinned/Recent zones. Source of
  // truth is `relay_workspaces/{id}.hidden_projects` on the same
  // workspace doc that already feeds pinnedFilters/projects above.
  const [hiddenProjects, setHiddenProjects] = useState<string[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  // Same lazy-load-on-first-open pattern as ccv2Mounted below — ProfilePanel is
  // rarely opened after initial setup, so it's excluded from the initial bundle.
  const [profileMounted, setProfileMounted] = useState(false);
  useEffect(() => { if (showProfile) setProfileMounted(true); }, [showProfile]);
  // track:track-backlog--cc-v2-phase-2-sprint-board-tab--cc-v2-left-nav-item
  const [showControlCenterV2, setShowControlCenterV2] = useState(false);
  const [ccv2Mounted, setCcv2Mounted] = useState(false);
  useEffect(() => { if (showControlCenterV2) setCcv2Mounted(true); }, [showControlCenterV2]);
  // Sprint 6 C4 (EFISSUE-817 Phase 2) — charter-first config. The
  // rules object lives on the same workspace doc; defaults match the
  // hardcoded constants so a fresh workspace renders identically.
  const [_charterRules, setCharterRules] = useState<CharterRules>(DEFAULT_CHARTER_RULES);
  const migrationDoneRef = useRef(false);
  // track:mention-roster (#51157) — who can be @mentioned: @relay, this
  // workspace's confirmed human members and its advisors (never spawned
  // build agents). Feeds the composer's @ picker and FloatingChat's
  // favorites picker.
  const { mentionables } = useMentionables(workspaceId);
  const mentionNames = useMemo(() => mentionables.map((m) => m.name), [mentionables]);

  // track:attention-dot-one-count (spec #49392): the sidebar dot uses the same
  // count as the Actions tab-bar badge (lib/actionsCount), so it is lit exactly
  // when the Actions tab has something.
  // track-backlog--perf-defer-useattentioncount-decisions-listener-rate-limit-listener-from-page-load-mount:
  // deferred until the Team Log's initial render completes (`loaded`) — the
  // hook subscribes to nothing on a falsy workspaceId.
  const actionsBadge = useActionsBadgeCount(loaded ? workspaceId : "");

  // Subscribe to pending modal queue count for action chime
  const [modalQueueCount, setModalQueueCount] = useState(0);
  useEffect(() => {
    const q = query(
      collection(db, 'relay_modal_queue', workspaceId, 'items'),
      where('state', '==', 'pending'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    return subscribeWithReconnect(({ retry, markConnected }) =>
      onSnapshot(q, snap => { markConnected(); setModalQueueCount(snap.size); },
        err => { console.warn('[App] modal queue count', err); retry(); })
    );
  }, [workspaceId]);

  // Chime: fire Tone A when a new [direct:travis] tagged message arrives
  const lastDirectSeqRef = useRef(0);
  useEffect(() => {
    if (!directChimeEnabledRef.current) return;
    const directMsgs = messages.filter(m => m.tags?.includes('direct:travis') && (m.sequence ?? 0) > 0);
    if (directMsgs.length === 0) return;
    const maxSeq = Math.max(...directMsgs.map(m => m.sequence ?? 0));
    if (maxSeq > lastDirectSeqRef.current) {
      if (lastDirectSeqRef.current > 0) playDirectChime();
      lastDirectSeqRef.current = maxSeq;
    }
  }, [messages, playDirectChime]);

  // Chime: fire Tone B when Actions tab pending count increases
  const prevActionCountRef = useRef<number | null>(null);
  useEffect(() => {
    if (!actionChimeEnabledRef.current) return;
    const total = modalQueueCount;
    if (prevActionCountRef.current === null) {
      prevActionCountRef.current = total;
      return;
    }
    if (total > prevActionCountRef.current) playActionChime();
    prevActionCountRef.current = total;
  }, [modalQueueCount, playActionChime]);

  const charterSeedDoneRef = useRef(false);
  useEffect(() => {
    const ref = doc(db, "relay_workspaces", workspaceId);
    return subscribeWithReconnect(({ retry, markConnected }) => onSnapshot(ref, snap => {
      markConnected();
      if (!snap.exists()) {
        setPinnedFilters([]);
        setProjects([]);
        setHiddenProjects([]);
        setActiveProjectSlug(null);
        setCharterRules(DEFAULT_CHARTER_RULES);
        return;
      }
      const data = snap.data();
      const rawPins = data?.pinned_filters;
      const nextPins = Array.isArray(rawPins) ? (rawPins as string[]) : [];
      setPinnedFilters(nextPins);

      const rawProjects = data?.projects;
      const nextProjects = Array.isArray(rawProjects) ? (rawProjects as ProjectRecord[]) : [];
      setProjects(nextProjects);

      const rawActiveSlug = data?.active_project_slug;
      setActiveProjectSlug(typeof rawActiveSlug === "string" ? rawActiveSlug : null);

      const rawHidden = data?.hidden_projects;
      setHiddenProjects(Array.isArray(rawHidden) ? (rawHidden as string[]) : []);


      // Charter rules: merge against defaults so a missing key never
      // breaks the panel; seed the doc once if any of the five keys
      // are absent or wrong-shaped.
      const rawCharter = data?.charter_rules;
      setCharterRules(mergeCharterRules(rawCharter));
      if (!charterSeedDoneRef.current && needsSeed(rawCharter)) {
        charterSeedDoneRef.current = true;
        console.log("[Relay] seeding charter_rules with defaults");
        setDoc(
          ref,
          { charter_rules: DEFAULT_CHARTER_RULES },
          { merge: true },
        ).catch(err => {
          console.error("[Relay] charter_rules seed failed:", err);
          charterSeedDoneRef.current = false;
        });
      }

      // Migration runs at most once per tab. computeMigration is
      // idempotent — once `projects` is non-empty it returns null.
      if (!migrationDoneRef.current) {
        const ops = computeMigration(nextPins, nextProjects);
        if (ops) {
          migrationDoneRef.current = true;
          console.log("[Relay] migrating projects:", ops, "from pinned_filters:", nextPins);
          setDoc(
            ref,
            {
              projects: arrayUnion(...ops.projectsToAdd),
              pinned_filters: arrayRemove(...ops.pinsToRemove),
            },
            { merge: true },
          ).catch(err => {
            console.error("[Relay] projects migration failed:", err);
            migrationDoneRef.current = false;
          });
        }
      }
    }, err => {
      console.error("[Relay] workspace doc subscription failed:", err);
      retry();
    }));
  }, []);
  const pinTag = async (tag: string) => {
    const ref = doc(db, "relay_workspaces", workspaceId);
    try {
      await setDoc(
        ref,
        { pinned_filters: arrayUnion(tag) },
        { merge: true },
      );
    } catch (err) {
      console.error("[Relay] pin failed for", tag, err);
    }
  };
  const unpinTag = async (tag: string) => {
    const ref = doc(db, "relay_workspaces", workspaceId);
    try {
      await updateDoc(ref, { pinned_filters: arrayRemove(tag) });
    } catch (err) {
      console.error("[Relay] unpin failed for", tag, err);
    }
  };
  const toggleProjectVisibility = useCallback(async (name: string) => {
    const next = toggleProjectHidden(hiddenProjects, name);
    const ref = doc(db, "relay_workspaces", workspaceId);
    try {
      await setDoc(ref, { hidden_projects: next }, { merge: true });
    } catch (err) {
      console.error("[Relay] toggle project visibility failed for", name, err);
    }
  }, [hiddenProjects]);

  // Sprint 6 C3 (EFISSUE-820) — create a project + auto-post a system
  // message to the activity log via the server, which stamps the sequence.
  // EFISSUE-838 — append a sub-filter to a project's subFilters array.
  // Firestore's arrayUnion can't target a property of an object inside an
  // array, so we read-modify-write the whole `projects` array. Concurrent
  // edits from multiple tabs can race here; for V1 single-user that's
  // acceptable — flagged in the EFISSUE-838 pre-flight risks list.
  const createSubFilter = useCallback(
    async (projectName: string, subFilter: string) => {
      const wsRef = doc(db, "relay_workspaces", workspaceId);
      const snap = await getDoc(wsRef);
      const data = snap.data() ?? {};
      const current = (data.projects as ProjectRecord[]) ?? [];
      const updated = current.map((p) =>
        p.name === projectName
          ? { ...p, subFilters: [...(p.subFilters ?? []), subFilter] }
          : p,
      );
      await setDoc(wsRef, { projects: updated }, { merge: true });
    },
    [],
  );

  // track:track-backlog--project-creation-ux-fix — "+ new project" now opens
  // the Project Background modal in create mode (see ERProjects.tsx /
  // ProjectBackgroundModal.tsx) instead of an inline name+color form, so the
  // background fields the user fills in are seeded on creation rather than
  // via a redundant empty-then-edit round trip.
  const [creatingProject, setCreatingProject] = useState(false);
  const openCreateProject = useCallback(() => setCreatingProject(true), []);

  const handleCreateProject = useCallback(
    async (name: string, color: string, background: ProjectBackground) => {
      const wsRef = doc(db, "relay_workspaces", workspaceId);
      const record: ProjectRecord = { name, color, createdAt: Date.now() };
      await setDoc(wsRef, { projects: arrayUnion(record) }, { merge: true });

      try {
        const headers = { ...(await getAuthHeader()), "Content-Type": "application/json" };
        await fetch("/api/workspace/project-background", {
          method: "POST",
          headers,
          body: JSON.stringify({ projectId: slugify(name), name, ...background, isCreate: true }),
        });
      } catch (err) {
        console.error("[Relay] project background seed failed:", err);
      }
      setCreatingProject(false);

      // SEC-COUNTERS (spec #51075) — the server stamps the sequence (and
      // initialises a missing counter); the browser no longer touches
      // relay_counters.
      try {
        await postWithSequence(db, {
          // The server binds a signed-in human's `author` to their own
          // identity (assertAuthorMatchesCaller), so this system message is
          // posted under the creator rather than the old "claude-code".
          author: agentIdFromUser(user) ?? "",
          content: `Project ${name} created — tag messages, decisions, and artifacts with \`${name}\` to associate them.`,
          tags: ["relay-system", "onboarding", name],
          workspace_id: workspaceId,
        });
      } catch (err) {
        // Project record already wrote successfully; surface the post
        // failure but don't roll back the project itself.
        console.error("[Relay] project-create system message failed:", err);
      }
    },
    [user],
  );

  // Per-project sub-filter counts. Activity = messages whose tags include
  // the project name — ui/left-nav-project-tag-fix: OR-matched against
  // both the legacy bare tag and the current `project:`-prefixed
  // convention (Charter Sprint-80), same as TeamLogStream's projectNames
  // logic, so this badge count actually reflects what the Activity row
  // will show when clicked. Decisions = decisions whose `category` equals
  // the project name (Decision shape has no `tags` field; category is
  // free-form per types.ts and slots cleanly into project-as-category
  // framing).
  const projectCounts = useMemo(() => {
    const out: Record<string, { activity: number; decisions: number }> = {};
    for (const p of projects) {
      const projectTag = `project:${p.name}`;
      const activity = allMessages.filter((m) => m.tags?.includes(p.name) || m.tags?.includes(projectTag)).length;
      const dCount = decisions.filter((d) => d.category === p.name).length;
      out[p.name] = { activity, decisions: dCount };
    }
    return out;
  }, [projects, allMessages, decisions]);

  // relay-42 Phase 2a — Recent zone. Top 2 projects sorted by max
  // message timestamp (most-recent activity first), excluding the
  // currently-pinned set so Recent doesn't duplicate Pinned. For V1
  // every project is pinned, so Recent will be empty until Phase 2b's
  // hidden-from-Pinned mechanism lands; this keeps the JSX in place
  // so the section appears the moment it has data.
  const recentProjects = useMemo(() => {
    const lastByName = new Map<string, number>();
    for (const m of allMessages) {
      if (!m.tags || !m.timestamp) continue;
      const ts = Date.parse(m.timestamp);
      if (Number.isNaN(ts)) continue;
      for (const p of projects) {
        if (m.tags.includes(p.name)) {
          const prev = lastByName.get(p.name) ?? 0;
          if (ts > prev) lastByName.set(p.name, ts);
        }
      }
    }
    return filterVisibleNames(projects, hiddenProjects)
      .filter(p => lastByName.has(p.name))
      .map(p => ({ project: p, lastActive: lastByName.get(p.name)! }))
      .sort((a, b) => b.lastActive - a.lastActive)
      .slice(0, 2);
  }, [projects, allMessages, hiddenProjects]);

  // relay-42 Phase 2b — drop pins that match a hidden project name.
  // Non-project pins (e.g. `blocker`, `pre-flight`) are preserved.
  // The underlying `pinned_filters` array stays intact in Firestore;
  // restoring visibility re-surfaces the original pin.
  const visiblePinnedFilters = useMemo(
    () => filterVisibleTags(pinnedFilters, hiddenProjects),
    [pinnedFilters, hiddenProjects],
  );

  // relay-S4 Task C (seq #2123): filter projects by hiddenProjects before passing to ERProjects
  const visibleProjects = useMemo(
    () => filterVisibleNames(projects, hiddenProjects),
    [projects, hiddenProjects],
  );
  // ui/left-nav-project-tag-fix — every project name in the workspace
  // (hidden or not — a hidden project's messages should still match
  // correctly if reached via a pinned filter or #seq link), passed to
  // TeamLogStream so it knows which activeFilters entries are project
  // scopes eligible for the bare/`project:`-prefixed OR match.
  const projectNames = useMemo(() => projects.map((p) => p.name), [projects]);

  // track:track-backlog--project-modal-rebuild-three-tab-layout-with-prd-versioning
  // — left-nav "PRD vN ↗" badge. project.background (from the workspace
  // doc's `projects` array) doesn't carry live PRD data — that lives in the
  // separate relay_workspaces/{workspaceId}/projects/{slug} doc — so this
  // fetches the version summary for every project up front instead of
  // waiting for each one's modal to be opened.
  const [prdSummaries, setPrdSummaries] = useState<
    Record<string, { prd_version: number; prd_label: string; prd_last_tab: string }>
  >({});
  useEffect(() => {
    if (projectNames.length === 0) {
      setPrdSummaries({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeader();
        const res = await fetch("/api/workspace/project-backgrounds", { headers });
        const data = await res.json();
        if (!cancelled && res.ok && data.summaries) setPrdSummaries(data.summaries);
      } catch (err) {
        console.error("[Relay] project PRD summaries load failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [projectNames]);

  // track:log-jump — full-history posts for the active tag filter, from
  // GET /api/messages/by-tag (replaces the BigQuery search). Failed or empty
  // fetches are never cached; see useTagHistory.
  const serverTag = useMemo(() => serverTagForFilters(activeFilters, projectNames), [activeFilters, projectNames]);
  const tagHistory = useTagHistory(serverTag);

  // Merge full-history tag results with the live window when a tag filter is
  // active. Dedup by sequence; sort ascending. Falls back to allMessages when
  // no filter.
  const mergedFilterMessages = useMemo(() => {
    if (activeFilters.length === 0 || tagHistory.messages.length === 0) return allMessages;
    const seen = new Set<number>();
    const merged: TeamLogMessage[] = [];
    for (const m of [...tagHistory.messages, ...allMessages]) {
      if (m.sequence != null && seen.has(m.sequence)) continue;
      if (m.sequence != null) seen.add(m.sequence);
      merged.push(m);
    }
    return merged.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  }, [activeFilters, tagHistory.messages, allMessages]);

  const openProjectDecisions = useCallback((projectName: string) => {
    setDecisionsProjectFilter(projectName);
    setCenterView("decisions");
    // Clear competing filter modes so the project pill is the only
    // active scope on the Decisions view.
    setActiveFilters([]);
    setCompoundFilter(null);
    setNavFilter(null);
    setSdlcFilter({ project: projectName, key: "decisions" });
  }, []);
  // A project's "Backlog" sub-row previously routed through selectSdlcFilter
  // (target: "activity") — it applied a backlog/spec-ready tag filter to the
  // Activity stream but never actually navigated to the dedicated Backlog
  // page, despite the row's own label. Mirrors openProjectDecisions above,
  // including its dedicated xProjectFilter state (rather than reusing
  // sdlcFilter for the highlight check — isSdlcRowActive's own decisions
  // branch already establishes that pattern: a dedicated state var checked
  // against centerView, not the generic sdlcFilter fallback).
  const openProjectBacklog = useCallback((projectName: string) => {
    // BacklogPage's project selector is keyed by slug, not display name.
    setBacklogProjectFilter(slugify(projectName));
    setCenterView("backlog");
    setActiveFilters([]);
    setCompoundFilter(null);
    setNavFilter(null);
    setSdlcFilter({ project: projectName, key: "backlog" });
  }, []);
  // relay-S3-sdlcfilters — click handler for the new fixed SDLC sub-rows.
  // Activity routes through `toggleFilter(project.name)` (existing path
  // with project mutex); other keys set `sdlcFilter` and stay in the
  // activity stream with the OR-mode tag set applied. Decisions is
  // handled by its dedicated openProjectDecisions callback.
  const selectSdlcFilter = useCallback(
    (projectName: string, filter: SDLCFilter) => {
      if (filter.target === "decisions") {
        // Routed through openProjectDecisions above.
        return;
      }
      // Toggle off if clicking the already-active row.
      setSdlcFilter((prev) =>
        prev?.project === projectName && prev.key === filter.key
          ? null
          : { project: projectName, key: filter.key },
      );
      setActiveFilters([projectName]);
      setCompoundFilter(null);
      setNavFilter(null);
      setCenterView("activity");
    },
    [],
  );
  // relay-S3-sdlcfilters — delete a user-added custom sub-filter.
  // Read-modify-write on `relay_workspaces/default.projects` mirroring
  // `createSubFilter` (single-tab race-acceptable per EFISSUE-838 risk).
  // Fixed SDLC keys are filtered out at the ERProjects boundary; this
  // callback only fires for user-added filters.
  const deleteSubFilter = useCallback(
    async (projectName: string, subFilter: string) => {
      const wsRef = doc(db, "relay_workspaces", workspaceId);
      const snap = await getDoc(wsRef);
      const data = snap.data() ?? {};
      const current = (data.projects as ProjectRecord[]) ?? [];
      const updated = current.map((p) =>
        p.name === projectName
          ? { ...p, subFilters: (p.subFilters ?? []).filter((s) => s !== subFilter) }
          : p,
      );
      await setDoc(wsRef, { projects: updated }, { merge: true });
      // If the deleted sub-filter was active, drop it.
      setCompoundFilter((prev) =>
        prev?.project === projectName && prev.subFilter === subFilter ? null : prev,
      );
    },
    [],
  );

  // Sprint 48 — Project Background sidebar row: view/edit existing project background.
  // track:track-sprint-48-project-background-modal — background now lives in its own
  // doc at relay_workspaces/{workspaceId}/projects/{slug}, not the projects array item.
  // Falls back to the array's old `background` field on first read so nothing saved
  // under the prior shape is silently lost; every save writes only to the new doc.
  const [pendingViewProject, setPendingViewProject] = useState<ProjectRecord | null>(null);
  // Same lazy-load-on-first-open pattern as ccv2Mounted/profileMounted above —
  // ProjectBackgroundModal is only opened during project create/edit.
  const [projectBackgroundMounted, setProjectBackgroundMounted] = useState(false);
  useEffect(() => { if (creatingProject || pendingViewProject) setProjectBackgroundMounted(true); }, [creatingProject, pendingViewProject]);
  const openExistingBackground = useCallback(
    async (name: string) => {
      const proj = projects.find((p) => p.name === name) ?? null;
      if (!proj) return;
      const background = await fetchProjectBackground(name, proj.background);
      setPendingViewProject({ ...proj, background });
    },
    [projects],
  );
  // track:track-backlog--project-modal-rebuild-three-tab-layout-with-prd-versioning
  // — tab-scoped save. "background"/"requirements" bump the PRD version
  // server-side (see saveProjectBackgroundVersioned); "setup" does not.
  // Modal stays open (each tab saves independently now) — only the badge
  // updates in place from the response, no refetch needed.
  // track:project-background-page — fetchProjectBackground/saveProjectBackground
  // (src/lib/projectBackground.ts) do the actual GET/POST; this callback's
  // only remaining job is keeping the modal's single-slot pendingViewProject
  // state in sync. The `prev.name === name` guard is new: previously this
  // was safe by construction (only the modal ever called it, for whatever
  // project pendingViewProject already held), but now that
  // ProjectBackgroundPage's accordions save through the shared function
  // above (not through this callback), guarding here costs nothing and
  // prevents this callback from ever overwriting a *different* project's
  // data into pendingViewProject if it's ever reused for something else later.
  const saveExistingBackground = useCallback(
    async (name: string, background: ProjectBackground, tab: "setup" | "background" | "requirements") => {
      const versionedTab = tab === "background" || tab === "requirements" ? tab : undefined;
      const data = await saveProjectBackground(name, background, { versionedTab });
      setPendingViewProject((prev) =>
        prev && prev.name === name
          ? {
              ...prev,
              background: {
                ...background,
                prd_version: typeof data.prd_version === "number" ? data.prd_version : prev.background?.prd_version,
                prd_label: typeof data.prd_label === "string" ? data.prd_label : prev.background?.prd_label,
                prd_last_tab: tab,
              },
            }
          : prev,
      );
      if (typeof data.prd_version === "number") {
        const prdVersion = data.prd_version;
        const prdLabel = data.prd_label ?? "";
        setPrdSummaries((prev) => ({
          ...prev,
          [slugify(name)]: { prd_version: prdVersion, prd_label: prdLabel, prd_last_tab: tab },
        }));
      }
    },
    [],
  );

  // track:track-backlog--project-modal-rebuild-three-tab-layout-with-prd-versioning
  // — Requirements tab "Start Structured Interview →". Same prefill-composer
  // pattern as the Help panel's "Ask a question" (App.tsx ~line 2118): open
  // the chat, seed the draft, focus. Closes the project modal so the user
  // lands on the composer instead of it being covered.
  //
  // track:track-backlog--p0-relay-doesnt-respond-to-prd-interview-message-from-start-structured-interview-button
  // — openToTab("chat") only flips React state; FloatingChat's composer
  // (ERComposerV1) doesn't mount its relay:prefill-composer/focus-composer
  // listeners until that state change commits. Dispatching synchronously
  // here — when FloatingChat was previously closed — fired the events into
  // an empty listener set: the composer opened blank, nothing got typed,
  // nothing was ever posted, so @relay had nothing to respond to. Deferring
  // to the next frame lets the mount + listener registration land first.
  const startPrdInterview = useCallback((projectName: string) => {
    setPendingViewProject(null);
    setCreatingProject(false);
    floatingChatRef.current?.openToTab("chat");
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent("relay:prefill-composer", {
        detail: {
          content: `@relay I'm working on ${projectName}. Can you help me define my requirements with a structured interview?`,
          tags: [`project:${projectName}`],
        },
      }));
      window.dispatchEvent(new CustomEvent(FOCUS_COMPOSER_EVENT));
    });
  }, []);

  // track:cc-preflight-pending-cards (seq #43104) — same open/defer/prefill/focus
  // sequence as startPrdInterview above, generalized so any distant component
  // (here: ActionsTab's pre-flight-required card, several component boundaries
  // below App.tsx) can open the composer with a specific post ready to send,
  // without posting on Travis's behalf directly — he must still click send
  // himself. Threaded down as a prop the same way onOpenWorkspace already is,
  // rather than yet another ad hoc window event, since crossing exactly this
  // App.tsx→ControlCenter→ActionsTab boundary already has that established
  // pattern.
  const openComposerWithPrefill = useCallback((content: string, tags: string[]) => {
    floatingChatRef.current?.openToTab("chat");
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent("relay:prefill-composer", { detail: { content, tags } }));
      window.dispatchEvent(new CustomEvent(FOCUS_COMPOSER_EVENT));
    });
  }, []);

  // track:track-backlog--project-modal-rebuild-three-tab-layout-with-prd-versioning
  // — latest `prd-draft`-tagged Team Log post per project, for the
  // Requirements tab's "Import from @relay" button (paired with the
  // adk-relay prd-development interview skill, seq #25116). Reuses the
  // same OR-match against the legacy bare project tag and the current
  // `project:`-prefixed convention as projectCounts above.
  const latestPrdDraftByProject = useMemo(() => {
    const out: Record<string, { content: string; sequence: number }> = {};
    for (const p of projects) {
      const projectTag = `project:${p.name}`;
      let latest: { content: string; sequence: number } | null = null;
      for (const m of allMessages) {
        if (!m.tags?.includes("prd-draft")) continue;
        if (!m.tags.includes(p.name) && !m.tags.includes(projectTag)) continue;
        const sequence = m.sequence ?? 0;
        if (!latest || sequence > latest.sequence) latest = { content: m.content, sequence };
      }
      if (latest) out[p.name] = latest;
    }
    return out;
  }, [projects, allMessages]);

  // Sprint 11 Track 2 — Notification badge and ACP inbox badge removed from sidebar header.
  // Hooks commented out; may be restored if badges return in future tracks.
  // Sprint 21 Track 1 — Notification panel replaced by FloatingChat (mounted below).
  // relay-S2-3 — viewer-derived agent identity replaces the hardcoded
  // "travis" stub from D1-A (#925). The viewer's `relay_acp_queue` lives
  // at their derived agentId; modal action posts use the same string for
  // `author`. Empty when no user is signed in (no listener is mounted; see
  // useAcpInbox.ts header).
  const viewerAgentId = useMemo(() => agentIdFromUser(user) ?? "", [user]);

  // Charter Editor gating — Human Lead is the only role that can edit charter rules.
  const isOwner = memberRole === "lead";

  // track:charter-owner-only (spec #66091) — asked of the server, never decided
  // here. False until the answer arrives, so Relay Admin stays hidden meanwhile.
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  useEffect(() => {
    if (!user || !workspaceId) {
      setIsPlatformOwner(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchCharterRoleCards(workspaceId);
        if (!cancelled) setIsPlatformOwner(result.isPlatformOwner);
      } catch (err) {
        console.warn("[Relay] platform owner check failed:", err);
        if (!cancelled) setIsPlatformOwner(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, workspaceId]);

  // ccv2DefaultTab — when set, tells ControlCenter which tab to open to.
  // Cleared on CC close so manual opens start on Console. Renamed from
  // ccDefaultTab/CCTab (track:dev-3-v1-decom-entry-point) when v1's entry
  // point was removed — v2 is now the only target for these triggers.
  const [ccv2DefaultTab, setCcv2DefaultTab] = useState<CCv2Tab | undefined>(undefined);
  // Bumped on every "open on Actions" entry so a click while Control Center is already
  // open on another tab re-selects Actions (the tab only re-reads on a changed value).
  const [ccv2TabRequest, setCcv2TabRequest] = useState(0);
  const openControlCenterOnActions = useCallback(() => {
    setShowControlCenterV2(true);
    setCcv2DefaultTab('actions');
    setCcv2TabRequest(n => n + 1);
  }, []);

  // Subscribe to this workspace's relay_modal/{workspaceId} trigger. Deletes the trigger doc, writes the
  // item to relay_modal_queue (so Actions tab "Pending Approvals" shows it), and
  // opens Control Center to the Actions tab so Travis handles it there.
  useEffect(() => {
    const ref = doc(db, "relay_modal", workspaceId);
    return subscribeWithReconnect(({ retry, markConnected }) => onSnapshot(ref, snap => {
      markConnected();
      if (!snap.exists()) return;
      const data = snap.data();
      if (!data || typeof data.title !== "string") return;
      // Delete trigger doc so planner can fire next modal. Firestore rules
      // for relay_modal/{userId} are `allow write: if false` (server-only,
      // by design) — this client delete always fails permission-denied.
      // The doc is never actually removed, so every listener reconnect
      // redelivers the same still-existing doc. Deduping below is what
      // actually stops the flood; this delete attempt is kept in case the
      // rules are relaxed later, but nothing here should depend on it
      // succeeding.
      deleteDoc(ref).catch(err => console.error("[Relay] modal trigger delete failed:", err));
      // Dedup key derived from the trigger doc's own write timestamp — not
      // relay_modal_queue item state. A stuck/undeletable trigger doc gets
      // redelivered on every reconnect; without a content-based dedup key,
      // each redelivery created a brand-new relay_modal_queue item forever
      // from a single show_modal() call (incident: 500+ duplicate "pending"
      // items in under 90 minutes from one trigger). Checking existence
      // first — rather than always setDoc — also guarantees a stale
      // redelivery can never resurrect an item a human already resolved.
      const queueItemRef = doc(db, "relay_modal_queue", workspaceId, "items", `modal-${deriveModalTriggerKey(data)}`);
      getDoc(queueItemRef).then(existing => {
        if (existing.exists()) return;
        // Route to Actions tab instead of floating modal — only for a
        // genuinely new trigger, not a stale redelivery of one already converted.
        setShowControlCenterV2(true);
        setCcv2DefaultTab('actions');
        return setDoc(queueItemRef, {
          ...queueItemFromModalTrigger(data),
          state: "pending",
          createdAt: serverTimestamp(),
        });
      }).catch(err => console.error("[Relay] modal queue write failed:", err));
    }, err => {
      console.error("[Relay] modal subscription failed:", err);
      retry();
    }));
  }, [workspaceId]);

  // On page load: open CC to Actions tab if there are pending queue items.
  useEffect(() => {
    const itemsCol = collection(db, "relay_modal_queue", workspaceId, "items");
    const q = query(itemsCol, where("state", "==", "pending"));
    getDocs(q).then(snap => {
      if (!snap.empty) {
        setShowControlCenterV2(true);
        setCcv2DefaultTab('actions');
      }
    }).catch(() => {});
  }, [workspaceId]);

  // Unread counts per pinned tag. Per-tag `seen_seq` lives in
  // localStorage — bumped to the current stream's max matching
  // sequence when the user clicks that pin. Count = matching,
  // unacknowledged messages whose sequence exceeds seen_seq.
  // `seenVersion` is a bump that forces recompute when a pin gets
  // marked seen (since localStorage reads aren't a React dep).
  const [seenVersion, setSeenVersion] = useState(0);
  const unreadCounts = useMemo(() => {
    const projectNameSet = new Set(projectNames);
    const out: Record<string, number> = {};
    for (const tag of pinnedFilters) {
      let seen = 0;
      try {
        const raw = localStorage.getItem(`relay_seen_seq_${tag}`);
        if (raw) seen = parseInt(raw, 10) || 0;
      } catch {}
      // ui/left-nav-project-tag-fix — a pinned project name (e.g. "relay")
      // OR-matches both tag forms, same as TeamLogStream/projectCounts.
      const matchesTag = (m: TeamLogMessage): boolean =>
        projectNameSet.has(tag)
          ? !!m.tags?.includes(tag) || !!m.tags?.includes(`project:${tag}`)
          : !!m.tags?.includes(tag);
      out[tag] = allMessages.filter(m =>
        matchesTag(m)
        && m.sequence != null
        && m.sequence > seen
        && !(m.acknowledged_by?.includes(myUid)),
      ).length;
    }
    return out;
    // `seenVersion` intentionally in deps so mark-seen triggers a recount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMessages, pinnedFilters, myUid, seenVersion, projectNames]);

  const markTagSeen = (tag: string) => {
    const maxSeq = allMessages
      .filter(m => m.tags?.includes(tag) && m.sequence != null)
      .reduce((mx, m) => Math.max(mx, m.sequence!), 0);
    try {
      localStorage.setItem(`relay_seen_seq_${tag}`, String(maxSeq));
    } catch {}
    setSeenVersion(v => v + 1);
  };

  // Click a pinned filter → toggle the activeFilters entry, and if
  // this is the "add" side of the toggle, mark the tag as seen so
  // the unread badge clears.
  const togglePinnedFilter = (tag: string) => {
    const wasActive = activeFilters.includes(tag);
    toggleFilter(tag);
    if (!wasActive) markTagSeen(tag);
  };

  // Searchable tag list for the pin-a-tag input. Union of the stream's
  // actually-used tags (derived from `allMessages`) with the hardcoded
  // `KNOWN_TAGS` seed. Per planner bug fix `o1X8n7mnQCoh4qGzcH0p`: the
  // prior implementation only exposed pre-colored tags — any tag that
  // someone used in a post but wasn't in the color map silently failed
  // to show as a pinnable option.
  const searchableTags = useMemo(() => {
    const set = new Set<string>(KNOWN_TAGS);
    for (const m of allMessages) {
      if (!m.tags) continue;
      for (const t of m.tags) {
        if (typeof t === "string" && t.length > 0) set.add(t);
      }
    }
    return Array.from(set).sort();
  }, [allMessages]);

  // Sidebar section collapsed-state. Persists to localStorage so the
  // user's preferred shape sticks across sessions. Pinned-filters
  // section is the only collapsible group right now; future Projects
  // / Decisions sections fit the same shape.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<SidebarCollapsed>(() => {
    try {
      const raw = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch { return {}; }
  });
  const togglePinnedCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = { ...prev, pinned: !prev.pinned };
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const toggleAlwaysCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = { ...prev, always: !prev.always };
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const loadEarlier = async (targetTimestamp?: number) => {
    if (loadingEarlier || !hasMoreEarlier) return;
    // Page by sequence, not timestamp: timestamp mixes Firestore Timestamp and
    // ISO-string values, which sort apart and leave gaps (service.ts getMessages).
    const oldest = oldestLoadedSequence(earlierMessages, messages, loadedFromCache);
    if (oldest == null) return;
    setLoadingEarlier(true);
    try {
      // Timeline Rail shows unfiltered message timeline. Filtering affects display only, not what's loaded.
      // fetchEarlierPage keeps the workspace_id filter firestore.rules requires
      // and uses the (workspace_id, sequence DESC) composite index.
      const page = await fetchEarlierPage(workspaceId, oldest, PAGE_SIZE);
      page.reverse(); // asc order matching the live listener
      setEarlierMessages(prev => [...page, ...prev]);
      if (page.length < PAGE_SIZE) setHasMoreEarlier(false);

      // Find and navigate to closest message to target
      if (targetTimestamp) {
        const allMessages = [...page, ...earlierMessages, ...messages];

        // Find message with minimum time difference from target
        const closest = allMessages.reduce((best, m) => {
          const mTime = Date.parse(m.timestamp);
          const bestTime = Date.parse(best.timestamp);
          return Math.abs(mTime - targetTimestamp) < Math.abs(bestTime - targetTimestamp)
            ? m
            : best;
        });

        const closestTime = Date.parse(closest.timestamp);
        const hoursDiff = Math.abs(closestTime - targetTimestamp) / (1000 * 60 * 60);

        // Bug Fix 2: Use else-if to avoid cascading messages, and always scroll
        if (hoursDiff > 48) {
          console.log(`Nearest message is from ${new Date(closestTime).toLocaleDateString()}`);
        } else if (hoursDiff > 24) {
          console.log("No messages found near that date");
        }

        // Always scroll to provide visual feedback for user's click
        scrollToMessage(closest.id);

        // If target still not reached after one load
        if (closestTime > targetTimestamp && hasMoreEarlier) {
          console.log(`Loaded ${PAGE_SIZE} earlier messages — click Load Earlier for more`);
        }
      }
    } catch (err) {
      console.error("[Relay] load earlier failed:", err);
    } finally {
      setLoadingEarlier(false);
    }
  };

  // Click-to-scroll + 1.2s purple outline flash on the target message.
  // Messages are marked with data-message-id in TeamLogStream; lookup by
  // querySelector keeps App.tsx free of a per-message ref map.
  const [flashedId, setFlashedId] = useState<string | null>(null);
  const scrollToMessage = (id: string) => {
    // Virtualized rows outside the current window aren't in the DOM, so try
    // the virtualizer's index-based scroll first; fall back to querySelector
    // for the eager (non-virtualized) render path.
    const scrolledViaVirtualizer = logViewRef.current?.scrollToMessageId(id);
    if (!scrolledViaVirtualizer) {
      const host = streamRef.current;
      if (!host) return;
      const target = host.querySelector(`[data-message-id="${CSS.escape(id)}"]`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
    setFlashedId(id);
    window.setTimeout(() => {
      setFlashedId(current => (current === id ? null : current));
    }, FLASH_DURATION_MS);
  };
  // relay-47 / backlog seq #30493 / track:log-jump — open a post by its
  // sequence. Used by inline `Re: #NNN` chips in TeamLogStream, sidebar
  // #-number search, and every "Action needed" SeqLink in Control Center v2.
  // A post already visible in the main Activity view is just scrolled to and
  // flashed. Anything else — not loaded, or hidden by an active filter —
  // switches to the Activity view and opens the jump view: the post with the
  // posts around it (GET /api/messages/window), highlighted, with a Back to
  // live button. Filters are suspended in the jump view and untouched by it,
  // so Back to live restores them. A sequence that doesn't exist shows
  // "No post #N. The Log runs from #A to #B." in the main view.
  const scrollToSequence = async (seq: number) => {
    setCenterView("activity");
    const inJump = logJump.jump?.messages.find(m => m.sequence === seq);
    if (inJump) {
      scrollToMessage(inJump.id);
      return;
    }
    const filtersActive =
      effectiveFilters.length > 0 || activeFilters.length > 0 || !!tagQuery.trim() || !!sdlcTagSet?.length;
    const local = allMessages.find(m => m.sequence === seq);
    if (local && !logJump.jump && !filtersActive) {
      scrollToMessage(local.id);
      return;
    }
    const id = await logJump.jumpTo(seq);
    if (id) window.setTimeout(() => scrollToMessage(id), 50);
  };
  // Sidebar #-number search — same resolver as scrollToSequence, kept as a
  // separate name since it's what SidebarTagFilter's onSequenceSearch prop
  // already expects.
  const searchBySequence = scrollToSequence;
  // relay-47 — start a reply targeting a specific sequence. The composer
  // listens for `relay:reply-to-message` and threads the next post.
  const startReplyTo = (seq: number) => {
    floatingChatRef.current?.openToTab("chat");
    window.dispatchEvent(
      new CustomEvent("relay:reply-to-message", { detail: { sequence: seq } }),
    );
  };

  const streamRef = useRef<HTMLDivElement>(null);
  // track-backlog--virtualize-activity-log — imperative handle into the
  // virtualized LogView, used to scroll to a message id that may currently
  // be outside the rendered window (querySelector alone can't find it).
  const logViewRef = useRef<TeamLogStreamHandle>(null);
  const atBottomRef = useRef(true);
  const floatingChatRef = useRef<FloatingChatHandle>(null);
  const [notesTasksOpen, setNotesTasksOpen] = useState(false);
  // track:build-a-spec-cc-nav-modal (seq #41984) — floats over the app,
  // doesn't navigate away, mirrors notesTasksOpen's controlled-panel pattern.
  const [buildASpecOpen, setBuildASpecOpen] = useState(false);

  // track:track-hrw-2 (RELAY_F2) — #slug deep links open How Relay Works with
  // that panel. On mount and on hashchange; the page itself writes the hash.
  const [hrwSlug, setHrwSlug] = useState<string | null>(null);

  // track:onboard-checklist (spec #61113) — Get started panel; opens itself on a member's first sign-in.
  const [getStartedOpen, setGetStartedOpen] = useState(false);
  useEffect(() => {
    if (centerView !== "how-relay-works") return;
    (async () => {
      try {
        await fetch("/api/workspace/onboarding/how-relay-works-opened", {
          method: "POST",
          headers: { ...(await getAuthHeader()), "Content-Type": "application/json" },
        });
      } catch (err) {
        console.error("[Onboarding] how-relay-works-opened failed:", err);
      }
    })();
  }, [centerView]);
  const openGetStartedTarget = useCallback((target: string) => {
    if (target.startsWith("how:")) {
      const slug = target.slice(4);
      setCenterView("how-relay-works");
      setNavFilter(null);
      if (slug === "overview") { setHrwSlug(null); return; }
      setSlugHash(slug);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
      return;
    }
    if (target === "sprint") {
      setShowControlCenterV2(true);
      return;
    }
    setCenterView(target === "projects" ? "project-background" : (target as CenterView));
    setNavFilter(null);
  }, []);
  useEffect(() => {
    const openFromHash = () => {
      try {
        // TAF-B: #feature-{id}[/outcome-{n}] deep-links into Test-A-Feature.
        if (/^#feature-/.test(window.location.hash)) {
          setCenterView("feature-testing");
          setNavFilter(null);
          return;
        }
        const slug = slugFromHash(window.location.hash);
        if (!slug) return;
        setCenterView("how-relay-works");
        setNavFilter(null);
        setHrwSlug(slug);
      } catch (err) {
        console.error("hash deep link failed", err);
      }
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, []);

  // Go there: slug → destination. Keyed by slug; the button copy comes from the module.
  const hrwGoThere = useCallback((slug: string, secondary?: boolean) => {
    const openCC = (tab: CCv2Tab) => {
      setShowControlCenterV2(true);
      setCcv2DefaultTab(tab);
    };
    setNavFilter(null);
    switch (slug) {
      case "set-up":
      case "team-and-keys":
        setCenterView("team");
        break;
      case "describe-the-project":
        setCenterView("project-background");
        break;
      case "spec-the-work":
        setBuildASpecOpen(true);
        break;
      case "write-the-tests":
      case "test-the-feature":
        setCenterView("feature-testing");
        break;
      case "board-it":
        openCC("backlog");
        break;
      case "build-and-review":
        openCC(secondary ? "actions" : "sprint-board");
        break;
      case "deliver-it":
        openCC("sprint-board");
        break;
      case "rules":
        setCenterView("charter");
        break;
      case "at-relay":
        // Same open/defer/focus sequence as startPrdInterview: the composer
        // only registers its focus listener after the chat tab mounts. With
        // no chat panel to open, fall back to All Activity.
        if (floatingChatRef.current) {
          floatingChatRef.current.openToTab("chat");
          requestAnimationFrame(() => window.dispatchEvent(new CustomEvent(FOCUS_COMPOSER_EVENT)));
        } else {
          setCenterView("activity");
        }
        break;
      default:
        break;
    }
  }, []);

  // track-backlog--floating-chat-auto-opens-for-new-users-on-first-login
  useEffect(() => {
    if (isFirstLogin) {
      floatingChatRef.current?.openToTab("chat");
    }
  }, [isFirstLogin]);

  // relay-S4 Task B (seq #2118): preserve scroll position across view mode changes
  const savedScrollRef = useRef<number>(0);
  // track-g: fraction (0..1) of log scroll position for density rail sync
  const [railScrollFraction, setRailScrollFraction] = useState(1);

  // S2-4 PR-3 — team_log subscription moved into `useTeamLogStream`.
  // Hook handles auth-state resubscribe, visibility-driven reconnect,
  // optimistic insert/rollback, and the 200-message cap.

  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      atBottomRef.current = distanceFromBottom < 120;
      // EFISSUE-793: show the floating scroll-to-bottom button when user
      // has scrolled up far enough that the next message would be off-
      // screen. 200px matches the Claude.ai desktop app's threshold.
      setShowScrollToBottom(distanceFromBottom > 200);
      // track-g: keep density rail active indicator in sync with log scroll
      const scrollable = el.scrollHeight - el.clientHeight;
      setRailScrollFraction(scrollable > 0 ? el.scrollTop / scrollable : 1);
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;
    if (atBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // relay-S4 Task B (seq #2118): restore scroll position after viewMode changes
  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;

    // Restore saved scroll position after viewMode changes
    requestAnimationFrame(() => {
      if (savedScrollRef.current > 0) {
        el.scrollTop = savedScrollRef.current;
      }
    });

    // Save scroll position when viewMode is about to change
    return () => {
      if (el) savedScrollRef.current = el.scrollTop;
    };
  }, [viewMode]);

  // Scroll to newest (bottom) when navigating back to activity view
  useEffect(() => {
    const el = streamRef.current;
    if (el && centerView === "activity") {
      setTimeout(() => {
        el.scrollTop = el.scrollHeight;
      }, 50);
    }
  }, [centerView]);

  // Track 16 — Tier limit awareness: Firestore subscription for rate limit data
  // track-backlog--perf-defer-useattentioncount-decisions-listener-rate-limit-listener-from-page-load-mount:
  // only ever displayed in ProfilePage (centerView==='profile'), so gate on
  // profileMounted — the same lazy-load-on-first-open flag ProfilePage
  // already relies on elsewhere in this file.
  useEffect(() => {
    if (!user || !profileMounted) return;

    const today = new Date().toISOString().split('T')[0];

    // Subscribe to rate limit doc
    const rateLimitDoc = doc(db, "relay_rate_limits", `${workspaceId}_${today}`);
    const workspaceDoc = doc(db, "relay_workspaces", workspaceId);

    const unsubRateLimit = onSnapshot(rateLimitDoc, async (snap) => {
      // Fetch workspace tier to determine limit
      const workspaceSnap = await getDoc(workspaceDoc);
      const tier = workspaceSnap.exists() ? (workspaceSnap.data()?.tier || "free") : "free";
      const tierLimits: Record<string, number> = { free: 500, starter: 500, pro: 2000, enterprise: 10000 };
      const limit = tierLimits[tier] || 500;

      if (!snap.exists()) {
        setRateLimitData({ count: 0, limit, tier });
        return;
      }

      const count = snap.data()?.count || 0;
      setRateLimitData({ count, limit, tier });

      // Check if 80% threshold crossed and warning not yet shown today
      const usagePercent = (count / limit) * 100;
      if (usagePercent >= 80 && !usageWarningShown) {
        setUsageWarningShown(true);
        // Store warning shown state in localStorage to prevent repeated checks
        try {
          localStorage.setItem(`relay_usage_warning_${today}`, "shown");
        } catch {}
      }
    });

    return () => {
      unsubRateLimit();
    };
  }, [user, usageWarningShown, profileMounted]);

  // Track 16 — Reset warning shown state daily
  useEffect(() => {
    const checkDate = () => {
      const today = new Date().toISOString().split('T')[0];
      const lastWarningDate = localStorage.getItem("relay_usage_warning_date");
      if (lastWarningDate !== today) {
        setUsageWarningShown(false);
        try {
          localStorage.setItem("relay_usage_warning_date", today);
        } catch {}
      }
    };
    checkDate();
    const interval = setInterval(checkDate, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  function scrollToBottom() {
    const el = streamRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }

  // NAV-ORDER-A: default Always order is the order of this list. Relay Admin keeps
  // its founder-only visibility rule (UI-layer defense only; the Charter
  // routes return 403 to anyone else).
  const navRows: NavRowDef[] = [
    {
      id: "control-center",
      label: "Control Center",
      active: showControlCenterV2,
      // Jumps straight to the Actions tab; the dot mirrors the Actions tab-bar badge.
      onClick: openControlCenterOnActions,
      dotDanger: actionsBadge.count > 0,
      visible: true,
    },
    {
      id: "all-activity",
      label: "All Activity",
      active: centerView === "activity" && !navFilter,
      onClick: () => {
        if (navFilter) toggleNavFilter(navFilter);
        setCenterView("activity");
      },
      visible: true,
    },
    {
      id: "decisions",
      label: "Decisions",
      active: centerView === "decisions",
      onClick: () => {
        setDecisionsProjectFilter(null);
        setCenterView("decisions");
      },
      visible: true,
    },
    {
      id: "create-a-project",
      label: "Create-A-Project",
      active: centerView === "project-background",
      onClick: () => {
        setCenterView("project-background");
        setNavFilter(null);
      },
      visible: true,
    },
    {
      // Floats over the app like Notes & Tasks; doesn't touch centerView.
      id: "build-a-spec",
      label: "Build-A-Spec",
      active: buildASpecOpen,
      onClick: () => setBuildASpecOpen((o) => !o),
      visible: true,
    },
    {
      // Full page via centerView "feature-testing" (display label only is "Test-A-Feature").
      id: "test-a-feature",
      label: "Test-A-Feature",
      active: centerView === "feature-testing",
      onClick: () => {
        setCenterView("feature-testing");
        setNavFilter(null);
      },
      visible: true,
    },
    {
      id: "backup-my-git",
      label: "Backup-My-Git",
      active: centerView === "backup-my-git",
      onClick: () => {
        setCenterView("backup-my-git");
        setNavFilter(null);
      },
      visible: true,
    },
    {
      id: "backlog",
      label: "Backlog",
      active: centerView === "backlog",
      onClick: () => {
        // The "all projects" entry point, not scoped to a project's own Backlog sub-row.
        setBacklogProjectFilter(null);
        setCenterView("backlog");
        setNavFilter(null);
      },
      visible: true,
    },
    {
      id: "workspace",
      label: "⚙ Workspace",
      active: centerView === "profile" || centerView === "charter" || centerView === "governance" || centerView === "project-mgmt",
      onClick: () => {
        setCenterView((prev) =>
          prev === "profile" || prev === "charter" || prev === "governance" || prev === "project-mgmt"
            ? prev
            : "profile"
        );
        setNavFilter(null);
      },
      visible: true,
    },
    {
      id: "team",
      label: "Team",
      active: centerView === "team",
      onClick: () => {
        setCenterView("team");
        setNavFilter(null);
      },
      visible: true,
    },
    {
      // Floats over the app; `open` is lifted state shared with FloatingNotesTasks.
      id: "notes-tasks",
      label: "Notes & Tasks",
      active: notesTasksOpen,
      onClick: () => setNotesTasksOpen((o) => !o),
      visible: true,
    },
    {
      id: "get-started",
      label: "Get started",
      active: getStartedOpen,
      onClick: () => setGetStartedOpen(true),
      visible: true,
    },
    {
      id: "how-relay-works",
      label: "How Relay Works",
      active: centerView === "how-relay-works",
      onClick: () => {
        setCenterView("how-relay-works");
        setNavFilter(null);
        setHrwSlug(null);
      },
      visible: true,
    },
    {
      id: "relay-admin",
      label: "Relay Admin",
      active: centerView === "relay-admin",
      onClick: () => {
        setCenterView("relay-admin");
        setNavFilter(null);
      },
      visible: isPlatformOwner,
    },
  ];
  const navOrder = useNavOrder(navRows);

  return (
    <div
      className={`er-root ${theme === "dark" ? "er-dark" : ""} app h-screen w-screen`}
      style={{ color: "var(--text)" }}
    >
      <aside
        className="sidebar flex flex-col"
        style={{
          width: "var(--sb-w)",
          flexShrink: 0,
          gridColumn: 1,
          gridRow: 2,
          borderRight: "1px solid var(--teal)",
          background: "var(--paper)",
        }}
      >
        {/* Removed: WorkspaceIndicator per UI-1 task 5 */}
        <nav className="flex-1 px-3 py-3" style={{ color: "var(--text2)", overflowY: "auto" }}>
          {/* Always rows: ids and order are the default; the member's saved order
              (NAV-ORDER-A, relay_nav_order) is applied by SidebarNav. Each row's
              label, onClick and active test are unchanged from the former
              hard-coded JSX. */}
          <SidebarNav
            rows={navRows}
            alwaysOrder={navOrder.alwaysOrder}
            zoneOrder={navOrder.zoneOrder}
            onAlwaysOrder={navOrder.setAlwaysOrder}
            onZoneOrder={navOrder.setZoneOrder}
            alwaysCollapsed={!!sidebarCollapsed.always}
            onToggleAlways={toggleAlwaysCollapsed}
            projects={(
            <ERProjects
            projects={visibleProjects}
            countsByProject={projectCounts}
            activeFilters={activeFilters}
            onToggleFilter={toggleFilter}
            onOpenDecisions={openProjectDecisions}
            onOpenBacklog={openProjectBacklog}
            onOpenCreateProject={openCreateProject}
            onToggleSubFilter={toggleSubFilter}
            onCreateSubFilter={createSubFilter}
            onDeleteSubFilter={deleteSubFilter}
            activeCompoundFilter={compoundFilter}
            sdlcFilters={SDLC_FILTERS}
            activeSdlcFilter={sdlcFilter}
            onSelectSdlcFilter={selectSdlcFilter}
            onOpenBackground={openExistingBackground}
            centerView={centerView}
            decisionsProjectFilter={decisionsProjectFilter}
            backlogProjectFilter={backlogProjectFilter}
            prdByProject={prdSummaries}
          />
            )}
            filters={(
            <ERPinnedFilters
            pinned={visiblePinnedFilters}
            unreadCounts={unreadCounts}
            activeFilters={activeFilters}
            searchableTags={searchableTags}
            onTogglePin={togglePinnedFilter}
            onPinTag={pinTag}
            onUnpinTag={unpinTag}
            collapsed={!!sidebarCollapsed.pinned}
            onToggleCollapsed={togglePinnedCollapsed}
          />
            )}
          />

          {/* Recent zone — top 2 projects by most-recent activity.
              Empty in V1 since every project is pinned; populates
              after Phase 2b's Settings → Projects hide toggle ships. */}
          {recentProjects.length > 0 && (
            <>
              <div className="er-sidebar-group-label" style={{ marginTop: 4 }}>Recent</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 4 }}>
                {recentProjects.map(({ project }) => (
                  <SidebarNavRow
                    key={project.name}
                    label={project.name}
                    active={activeFilters.includes(project.name)}
                    onClick={() => toggleFilter(project.name)}
                  />
                ))}
              </div>
            </>
          )}

          <div
            style={{
              borderTop: "1px solid var(--er-border)",
              margin: "10px 0 8px",
            }}
          />
          <SidebarTagFilter
            searchableTags={searchableTags}
            activeFilters={activeFilters}
            onToggleFilter={toggleFilter}
            onQueryChange={setTagQuery}
            onSequenceSearch={searchBySequence}
            viewMode={viewMode}
          />
          {(logJump.missing || logJump.error) && (
            <div style={{ fontSize: 11, color: "var(--ink-muted)", padding: "0 2px 8px" }}>
              {logJump.missing
                ? logJumpMissingText(logJump.missing.seq, logJump.missing.first, logJump.missing.last)
                : logJump.error}
            </div>
          )}
          <SidebarActiveFilters
            activeFilters={activeFilters}
            onToggleFilter={toggleFilter}
            onClearFilters={clearFilters}
            extraPill={
              centerView === "decisions" && decisionsProjectFilter
                ? {
                    tag: decisionsProjectFilter,
                    color:
                      projects.find(p => p.name === decisionsProjectFilter)?.color
                        ?? "var(--accent)",
                    onRemove: () => setDecisionsProjectFilter(null),
                  }
                : null
            }
            viewMode={viewMode}
          />

          <NavResetButton onReset={navOrder.reset} />
        </nav>
        <div className="px-3 py-2" style={{ borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
          {/* Stacked controls: Light Mode / @ Mention chime / ! Action chime */}
          <div style={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
            <button
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="sb-footer-ctrl"
            >
              <span aria-hidden style={{ fontSize: 13 }}>{theme === "dark" ? "☾" : "☀"}</span>
              <span>{theme === "dark" ? "Dark mode" : "Light mode"}</span>
            </button>
            <button
              onClick={toggleDirectChime}
              aria-label={directChimeEnabled ? "Disable direct mention chime" : "Enable direct mention chime"}
              title={directChimeEnabled ? "Disable direct mention chime" : "Enable direct mention chime"}
              className={`sb-footer-ctrl${directChimeEnabled ? "" : " muted"}`}
            >
              <span aria-hidden style={{ fontSize: 13 }}>@</span>
              <span>{directChimeEnabled ? "Mention chime" : "Mention muted"}</span>
            </button>
            <button
              onClick={toggleActionChime}
              aria-label={actionChimeEnabled ? "Disable action alert chime" : "Enable action alert chime"}
              title={actionChimeEnabled ? "Disable action alert chime" : "Enable action alert chime"}
              className={`sb-footer-ctrl${actionChimeEnabled ? "" : " muted"}`}
            >
              <span aria-hidden style={{ fontSize: 13 }}>!</span>
              <span>{actionChimeEnabled ? "Action chime" : "Action muted"}</span>
            </button>
          </div>
        </div>
      </aside>

      <header
        className="app-header flex items-center justify-between px-6"
        style={{
          position: "relative",
          height: "var(--header-h)",
          gridRow: 1,
          borderBottom: "2px solid var(--teal)",
          background: "var(--surface)",
        }}
        >
          <div className="logo-slot">
            <img src="/easyrelay-wordmark.svg" height="42" alt="easyrelay" style={{ display: "block" }} />
          </div>
          {loadedFromCache && (
            <span
              title="Showing cached Team Log — live data is loading"
              style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 8 }}
            >
              Updating…
            </span>
          )}
          {activeProjectName && (
            <span
              className="header-active-project"
              style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-muted, #6b7280)" }}
            >
              {activeProjectName}
            </span>
          )}
        </header>

      {centerView === "activity" && <DensityRail messages={allMessages} onScrollToMessage={scrollToMessage} activeFraction={railScrollFraction} hasMoreEarlier={hasMoreEarlier} loadingEarlier={loadingEarlier} onLoadEarlier={loadEarlier} />}
      {centerView === "activity" && <MarkersRail messages={allMessages} onNavigate={scrollToSequence} activeFraction={railScrollFraction} />}
      {centerView === "decisions" && <DecisionsMarkersRail decisions={mergedDecisions} scrollRef={mainScrollRef} />}
      {centerView === "backlog" && <BacklogPageMarkersRail items={backlogFilteredItems} />}
      {centerView === "team" && <TeamPageMarkersRail scrollRef={teamScrollRef} />}
      {centerView === "profile" && <AccountMarkersRail scrollRef={profileScrollRef} />}
      {centerView === "charter" && <CharterMarkersRail scrollRef={charterScrollRef} />}
      {centerView === "governance" && <WorkspaceGovernanceMarkersRail scrollRef={governanceScrollRef} />}
      {centerView === "project-mgmt" && <ProjectManagementMarkersRail scrollRef={projectMgmtScrollRef} />}
      {centerView === "feature-testing" && featureTestingSections.length > 0 && (
        <TestAFeatureMarkersRail sections={featureTestingSections} scrollRef={featureTestingScrollRef} />
      )}
      {centerView === "project-background" && projectBackgroundSections.length > 0 && (
        <ProjectBackgroundMarkersRail sections={projectBackgroundSections} scrollRef={projectBackgroundScrollRef} />
      )}

      <main
        ref={mainScrollRef}
        className="feed flex flex-col min-w-0"
        style={{ gridColumn: 4, gridRow: 2, overflowY: "auto", minHeight: 0 }}
      >
        <ErrorBoundary resetKey={centerViewResetKey}>
        <Suspense fallback={null}>
        {centerView === "backlog" ? (
          <BacklogPage
            workspaceId={workspaceId}
            onItemsFiltered={setBacklogFilteredItems}
            initialProjectFilter={backlogProjectFilter}
            projects={projects}
            onOpenSequence={scrollToSequence}
            onOpenPlanning={() => { setShowControlCenterV2(true); setCcv2DefaultTab('backlog'); }}
          />
        ) : centerView === "project-background" ? (
          <ProjectBackgroundPage
            workspaceId={workspaceId}
            projects={projects}
            existingNames={projects.map((p) => p.name.toLowerCase())}
            onCreate={handleCreateProject}
            onStartInterview={startPrdInterview}
            onScrollReady={setProjectBackgroundScrollEl}
            onSectionsChange={setProjectBackgroundSections}
          />
        ) : centerView === "feature-testing" ? (
          <FeatureTestingPage
            workspaceId={workspaceId}
            projects={projects}
            onScrollReady={setFeatureTestingScrollEl}
            onSectionsChange={setFeatureTestingSections}
          />
        ) : centerView === "backup-my-git" ? (
          <BackupMyGitPage />
        ) : centerView === "team" ? (
          <TeamPage workspaceId={workspaceId} onOpenControlCenter={openControlCenterOnActions} onScrollReady={setTeamScrollEl} />
        ) : centerView === "profile" || centerView === "charter" || centerView === "governance" || centerView === "project-mgmt" ? (
          <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
            <div
              style={{
                display: "flex",
                gap: 4,
                padding: "12px 24px 0",
                flexShrink: 0,
              }}
            >
              <PageTabs
                label="Workspace"
                active={centerView}
                onChange={(id) => setCenterView(id as typeof centerView)}
                tabs={[
                  { id: "profile", label: "Account" },
                  { id: "charter", label: "Charter" },
                  { id: "governance", label: "Workspace Governance" },
                  { id: "project-mgmt", label: "Project Management" },
                ]}
              />
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              {centerView === "profile" ? (
                <ProfilePage
                  user={user}
                  onSignOut={() => signOut()}
                  rateLimitData={rateLimitData}
                  onScrollReady={setProfileScrollEl}
                  onReopenSetup={() => {
                    setCenterView("team");
                    floatingChatRef.current?.openToTab("chat");
                  }}
                  allMemberships={allMemberships}
                  activeWorkspaceId={workspaceId}
                  onSwitchWorkspace={onSwitchWorkspace}
                  onWorkspaceCreated={onWorkspaceCreated}
                />
              ) : centerView === "charter" ? (
                <>
                  <CharterRoleCardsView workspaceId={workspaceId} />
                  <CharterEditor isOwner={isOwner} workspaceId={workspaceId} onScrollReady={setCharterScrollEl} />
                </>
              ) : centerView === "governance" ? (
                <WorkspaceGovernanceSection workspaceId={workspaceId} onScrollReady={setGovernanceScrollEl} />
              ) : (
                <ProjectManagementSection
                  projects={projects}
                  hiddenProjects={new Set(hiddenProjects)}
                  onToggleProjectVisibility={toggleProjectVisibility}
                  onScrollReady={setProjectMgmtScrollEl}
                />
              )}
            </div>
          </div>
        ) : centerView === "decisions" ? (
          <ERDecisionsPanel
            decisions={mergedDecisions}
            loaded={decisionsLoaded}
            error={decisionsError || bqDecisionsError}
            projectFilter={decisionsProjectFilter}
            projects={projects.map(p => p.name)}
            historyLoading={bqDecisionsLoading}
            historyCount={bqDecisionsLoaded && !bqDecisionsError ? bqDecisionRows.length : undefined}
            onRetry={bqDecisionsError ? () => { setBqDecisionRows([]); setBqDecisionsLoaded(false); } : undefined}
          />
        ) : centerView === "how-relay-works" ? (
          <HowRelayWorksPage initialSlug={hrwSlug} onSlugChange={setHrwSlug} onGoThere={hrwGoThere} />
        ) : centerView === "relay-admin" ? (
          <RelayAdmin isFounder={isPlatformOwner} workspaceId={workspaceId} />
        ) : (
        <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <section
            ref={streamRef}
            className="flex-1 overflow-auto px-6 py-4"
            style={{ background: "var(--bg)" }}
          >
            {error && (
              <div
                style={{
                  background: "var(--tag-blocker-bg)",
                  color: "var(--tag-blocker)",
                  border: "1px solid var(--tag-blocker)",
                  padding: "8px 12px",
                  borderRadius: 6,
                  fontSize: 12,
                  marginBottom: 12,
                }}
              >
                {error}
              </div>
            )}
            {!error && !loaded && (
              <div style={{ color: "var(--text3)", fontSize: 13, padding: "4px 0" }}>
                Loading…
              </div>
            )}
            {!error && loaded && allMessages.length === 0 && (
              <div style={{ color: "var(--text3)", fontSize: 13, lineHeight: 1.6 }}>
                No messages yet.{" "}
                <button
                  type="button"
                  onClick={() => {
                    floatingChatRef.current?.openToTab("chat");
                    window.dispatchEvent(new CustomEvent(FOCUS_COMPOSER_EVENT));
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--accent)",
                    fontSize: 13,
                    cursor: "pointer",
                    padding: 0,
                    fontFamily: "inherit",
                    textDecoration: "underline",
                  }}
                >
                  Post the first message →
                </button>
              </div>
            )}
            {logJump.jump && <LogJumpBanner seq={logJump.jump.seq} onBackToLive={logJump.backToLive} />}
            {!logJump.jump && logJump.loading && (
              <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-mono)", padding: "4px 0 8px" }}>
                Opening post…
              </div>
            )}
            {logJump.missing && (
              <LogJumpNotice
                text={logJumpMissingText(logJump.missing.seq, logJump.missing.first, logJump.missing.last)}
                onBackToLive={logJump.clearNotice}
              />
            )}
            {logJump.error && <LogJumpNotice text={logJump.error} onBackToLive={logJump.clearNotice} />}
            {allMessages.length > 0 && (
              <LogView
                ref={logViewRef}
                scrollParentRef={streamRef}
                messages={logJump.jump ? logJump.jump.messages : mergedFilterMessages}
                jumpActive={!!logJump.jump}
                activeFilters={logJump.jump ? [] : effectiveFilters}
                sdlcTagSet={logJump.jump ? null : sdlcTagSet}
                projectNames={projectNames}
                tagQuery={logJump.jump ? "" : tagQuery}
                onToggleFilter={toggleFilter}
                flashedId={logJump.jump ? (logJump.jump.messages.find(m => m.sequence === logJump.jump!.seq)?.id ?? flashedId) : flashedId}
                hasMoreEarlier={!logJump.jump && hasMoreEarlier}
                loadingEarlier={loadingEarlier}
                onLoadEarlier={loadEarlier}
                myUid={myUid}
                onScrollToSequence={scrollToSequence}
                tagHistory={logJump.jump || activeFilters.length === 0 ? undefined : { hasMore: tagHistory.hasMore, loading: tagHistory.loading, error: tagHistory.error, onLoadMore: tagHistory.loadMore }}
                onStartReply={startReplyTo}
                selfAuthor={myAuthor}
              />
            )}
          </section>

          {/* EFISSUE-793: scroll-to-bottom button. Appears when user is
               >200px above the bottom of the stream; hidden otherwise. */}
          {showScrollToBottom && (
            <button
              type="button"
              onClick={scrollToBottom}
              aria-label="Scroll to bottom"
              title="Scroll to bottom"
              style={{
                position: "absolute",
                bottom: 16,
                left: "50%",
                transform: "translateX(-50%)",
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "var(--surface2)",
                border: "1px solid var(--border)",
                color: "var(--text)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0.85,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                zIndex: 5,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = "0.85"; }}
            >
              {/* Down-chevron rendered inline so we don't pull in an icon lib */}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="4 6 8 10 12 6" />
              </svg>
            </button>
          )}
        </div>
        )}
        </Suspense>
        </ErrorBoundary>
      </main>

      <ERSettingsModal
        open={showSettings}
        projects={projects}
        hiddenProjects={hiddenProjects}
        onToggle={toggleProjectVisibility}
        onClose={() => setShowSettings(false)}
      />
      {projectBackgroundMounted && (
        <Suspense fallback={null}>
          <ProjectBackgroundModal
            open={creatingProject || !!pendingViewProject}
            mode={creatingProject ? "create" : "edit"}
            project={pendingViewProject}
            existingNames={projects.map((p) => p.name.toLowerCase())}
            latestPrdDraft={pendingViewProject ? latestPrdDraftByProject[pendingViewProject.name] ?? null : null}
            onCancel={() => { setCreatingProject(false); setPendingViewProject(null); }}
            onSave={saveExistingBackground}
            onCreate={handleCreateProject}
            onStartInterview={startPrdInterview}
          />
        </Suspense>
      )}
      {profileMounted && (
        <Suspense fallback={null}>
          <ProfilePanel
            open={showProfile}
            onClose={() => setShowProfile(false)}
            user={user}
            theme={theme}
            onToggleTheme={toggleTheme}
            projects={projects}
            hiddenProjects={new Set(hiddenProjects)}
            onToggleProjectVisibility={toggleProjectVisibility}
            onSignOut={() => signOut()}
          />
        </Suspense>
      )}
      {/* v1 Control Center panel removed — track:dev-3-v1-decom-entry-point.
          ControlCenter.tsx is left intact and unmounted for reference; not
          imported here. onProjectChange (project-filter switch) has no v2
          equivalent yet — flagged as an open gap in build-complete.
          onOpenWorkspace was wired separately in #495. */}
      {/* track:track-backlog--cc-v2-phase-2-sprint-board-tab--cc-v2-left-nav-item */}
      {ccv2Mounted && (
        <Suspense fallback={null}>
          <ControlCenter
            open={showControlCenterV2}
            onClose={() => { setShowControlCenterV2(false); setCcv2DefaultTab(undefined); }}
            workspaceId={workspaceId}
            onScrollToSequence={(seq) => { setShowControlCenterV2(false); scrollToSequence(seq); }}
            onOpenWorkspace={() => {
              setShowControlCenterV2(false);
              setCenterView((prev) => (prev === "charter" ? prev : "charter"));
              setNavFilter(null);
            }}
            onOpenComposerWithPrefill={openComposerWithPrefill}
            defaultActiveTab={ccv2DefaultTab}
            tabRequestKey={ccv2TabRequest}
            viewerAgentId={viewerAgentId}
            isHumanLead={isOwner}
          />
        </Suspense>
      )}
      <GetStartedPanel
        open={getStartedOpen}
        onClose={() => setGetStartedOpen(false)}
        onFirstVisit={() => setGetStartedOpen(true)}
        onNavigate={openGetStartedTarget}
      />
      <FloatingChat
        ref={floatingChatRef}
        viewerAgentId={viewerAgentId}
        workspaceId={workspaceId}
        user={user}
        participants={mentionNames}
        mentionables={mentionables}
        onOptimisticInsert={optimisticInsert}
        onOptimisticRollback={optimisticRollback}
        projects={projects}
        activeProjectSlug={activeProjectSlug}
        isHumanLead={isOwner}
      />
      <FloatingNotesTasks open={notesTasksOpen} onOpenChange={setNotesTasksOpen} />
      <BuildASpecModal open={buildASpecOpen} onOpenChange={setBuildASpecOpen} workspaceId={workspaceId} projects={projects} />
    </div>
  );
}

/**
 * SidebarTagFilter — Sprint 4 C2.1b (`7mm45UlyjleBpQmJCY6e`).
 *
 * Inline ad-hoc tag filter for the left sidebar. Type to prefix-match
 * against the same `searchableTags` set ERPinnedFilters uses; click a
 * match to toggle that tag in the parent's `activeFilters`. Same handler
 * wiring as the inline tag pills in the stream + the filter bar above
 * the stream — single source of truth for filter state lives in App
 * shell.
 */
function SidebarTagFilter({
  searchableTags,
  activeFilters,
  onToggleFilter,
  onQueryChange,
  onSequenceSearch,
  viewMode,
}: {
  searchableTags: string[];
  activeFilters: string[];
  onToggleFilter: (tag: string) => void;
  onQueryChange?: (q: string) => void;
  onSequenceSearch?: (seq: number) => void;
  viewMode?: ViewMode;
}) {
  const [query, setQuery] = useState("");
  const matches = query.trim().length === 0
    ? []
    : searchableTags
        .filter(t => t.toLowerCase().startsWith(query.trim().toLowerCase()))
        .slice(0, 6);
  return (
    <div style={{ marginBottom: 12, position: "relative" }}>
      <span
        aria-hidden
        style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-muted)", fontSize: 13, pointerEvents: "none" }}
      >⌕</span>
      <input
        type="text"
        value={query}
        onChange={e => {
          setQuery(e.target.value);
          onQueryChange?.(e.target.value);
        }}
        onKeyDown={e => {
          if (e.key === "Enter" && query.trim().length > 0) {
            const trimmed = query.trim();
            const seqMatch = /^#?(\d+)$/.exec(trimmed);
            if (seqMatch && onSequenceSearch) {
              onSequenceSearch(Number(seqMatch[1]));
            } else {
              onToggleFilter(trimmed);
            }
            setQuery("");
            onQueryChange?.("");
          }
        }}
        placeholder="Search log by tag or #seq…"
        className="er-browse-tags-input"
        disabled={viewMode === "chat"}
        style={{
          width: "100%",
          boxSizing: "border-box",
          fontSize: 12,
          padding: "6px 12px 6px 32px",
          borderRadius: "var(--radius-pill)",
          border: "1.25px solid var(--teal)",
          background: "var(--paper)",
          color: "var(--ink)",
          fontFamily: "var(--font-ui)",
          outline: "none",
          opacity: viewMode === "chat" ? 0.5 : 1,
          cursor: viewMode === "chat" ? "not-allowed" : "text",
        }}
      />
      {matches.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
          {matches.map(t => {
            const isActive = activeFilters.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => {
                  onToggleFilter(t);
                  setQuery("");
                  onQueryChange?.("");
                }}
                title={isActive ? `Remove filter: ${t}` : `Filter stream by: ${t}`}
                style={{
                  fontSize: 10,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "var(--surface2)",
                  color: "var(--text)",
                  border: "1px solid var(--border)",
                  fontFamily: "var(--font-mono)",
                  cursor: "pointer",
                  outline: isActive ? "2px solid var(--er-accent)" : "none",
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
      )}
      {matches.length === 0 && query.trim().length > 0 && (
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4, fontFamily: "var(--font-mono)" }}>
          Press Enter to filter by "{query.trim()}"
        </div>
      )}
    </div>
  );
}

/**
 * SidebarActiveFilters — Sprint 5 C1 (`hfZpdLwISuYwQhK2rJT3`,
 * EFISSUE-813). Renders the currently applied filter set inside the
 * sidebar. Replaces the old ERFilterBar above the stream so the center
 * column stays pure Activity Log. Only mounts when `activeFilters` is
 * non-empty. Each pill has a hover × to remove; "clear all" at the
 * bottom empties the set via `onClearFilters`.
 */
function SidebarActiveFilters({
  activeFilters,
  onToggleFilter,
  onClearFilters,
  extraPill,
  viewMode,
}: {
  activeFilters: string[];
  onToggleFilter: (tag: string) => void;
  onClearFilters: () => void;
  // relay-S3-uxfix Fix 2 — `openProjectDecisions` doesn't push the project
  // onto `activeFilters` (which scopes the activity stream). Instead, the
  // shell hands us a synthetic pill to render so the user sees the same
  // visual indicator as Activity-mode filters do — clicking the X clears
  // `decisionsProjectFilter` via the supplied callback.
  extraPill?: { tag: string; color: string; onRemove: () => void } | null;
  viewMode?: ViewMode;
}) {
  // relay-S4 Task A (seq #2078): Chat view bypasses activeFilters entirely.
  if (viewMode === "chat") return null;
  if (activeFilters.length === 0 && !extraPill) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div className="er-sidebar-group-label" style={{ marginBottom: 4 }}>
        Active filters
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {extraPill && (
          <ActiveFilterRow
            key={`extra:${extraPill.tag}`}
            tag={extraPill.tag}
            color={extraPill.color}
            onRemove={extraPill.onRemove}
          />
        )}
        {activeFilters.map(tag => {
          const style = tagPillStyle(tag);
          return (
            <ActiveFilterRow
              key={tag}
              tag={tag}
              color={style.color}
              onRemove={() => onToggleFilter(tag)}
            />
          );
        })}
        <button
          type="button"
          onClick={onClearFilters}
          style={{
            marginTop: 4,
            padding: "2px 6px",
            background: "transparent",
            border: 0,
            color: "var(--text3)",
            fontSize: 11,
            cursor: "pointer",
            textAlign: "left",
            textDecoration: "underline",
            fontFamily: "inherit",
          }}
        >
          clear all
        </button>
      </div>
    </div>
  );
}

function ActiveFilterRow({
  tag,
  color,
  onRemove,
}: {
  tag: string;
  color: string;
  onRemove: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="er-sidebar-item"
      onClick={onRemove}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="button"
      tabIndex={0}
      aria-label={`Remove ${tag}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRemove();
        }
      }}
      style={{ cursor: "pointer" }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFamily: "var(--font-mono)",
        }}
      >
        {tag}
      </span>
      {hover && (
        <span
          aria-hidden
          style={{
            color: "var(--teal-darker, #054750)",
            fontSize: 13,
            lineHeight: 1,
            padding: "0 2px",
          }}
        >
          ×
        </span>
      )}
    </div>
  );
}
